<template>
  <div class="rule-canvas-root d-flex flex-column fill-height">
    <!-- ════════════ CABECERA ════════════ -->
    <div class="rule-header d-flex align-center white flex-shrink-0 px-4">
      <v-menu offset-y :close-on-content-click="false" v-model="colorMenu">
        <template v-slot:activator="{ on }">
          <div
            class="rule-badge d-flex align-center justify-center white--text rounded-lg flex-shrink-0 cursor-pointer"
            :style="{ background: effectiveColor }"
            v-on="on"
            :title="$t('color')"
          >
            <v-icon dark size="20">mdi-lightning-bolt</v-icon>
          </div>
        </template>
        <v-card class="pa-3">
          <div
            class="text-caption font-weight-bold text-uppercase grey--text text--darken-1 mb-2 rc-label"
          >
            {{ $t("color") }}
          </div>
          <a-color-palette v-model="rule.color" @change="colorMenu = false" />
        </v-card>
      </v-menu>

      <div class="d-flex flex-column" style="gap: 3px;">
        <!-- El placeholder enseña el nombre que se pondrá solo al guardar si
             se deja en blanco: se ve antes de guardar, no después -->
        <v-text-field
          v-model="rule.name"
          :placeholder="autoRuleName"
          flat
          solo
          dense
          hide-details
          spellcheck="false"
          min-height="auto"
          background-color="grey lighten-4"
          class="rule-name-input"
        />
        <div>
          <span
            class="color-chip text-caption font-weight-bold text-uppercase grey--text text--darken-1 rounded d-inline-flex align-center pa-1 px-2 grey lighten-4 prevent-select cursor-pointer"
            @click="colorMenu = true"
          >
            <span
              class="color-chip-dot rounded flex-shrink-0"
              :style="{ background: effectiveColor }"
            ></span>
            {{ $t("color") }}
          </span>
        </div>
      </div>

      <v-spacer></v-spacer>

      <!-- Probar la regla contra los datos reales de una fecha, sin guardar.
           Sólo en entornos de desarrollo: el backend lo vuelve a validar. -->
      <v-menu
        v-if="rule_test_enabled"
        v-model="test_menu"
        :close-on-content-click="false"
        offset-y
        left
      >
        <template v-slot:activator="{ on, attrs }">
          <v-btn
            v-bind="attrs"
            v-on="on"
            color="warning"
            rounded
            outlined
            class="pl-5 pr-5"
          >
            <v-icon small class="mr-2">mdi-flask-outline</v-icon>
            {{ $t("test_rule") }}
          </v-btn>
        </template>
        <v-card width="290">
          <!-- Mismo selector de fecha que el resto de la aplicación -->
          <v-date-picker
            v-model="test_target_date"
            first-day-of-week="1"
            :min="today"
            full-width
          ></v-date-picker>
          <v-divider></v-divider>
          <div class="pa-3">
            <div class="text-caption grey--text mb-2">
              {{ $t("test_rule_hint") }}
            </div>
            <v-btn
              color="warning"
              rounded
              outlined
              block
              :loading="testing_rule"
              :disabled="!test_target_date"
              @click="onTestRule"
            >
              <v-icon small class="mr-2">mdi-flask-outline</v-icon>
              {{ $t("test_rule") }}
            </v-btn>
          </div>
        </v-card>
      </v-menu>

      <v-btn
        color=""
        text
        rounded
        depressed
        class="pl-5 pr-5"
        @click="onCancel"
      >
        {{ $t("cancel") }}
      </v-btn>
      <v-btn color="secondary" rounded class="pl-5 pr-5" @click="onSave">
        <v-icon left size="18">mdi-content-save-outline</v-icon>
        {{ $t("save") }}
      </v-btn>
    </div>

    <!-- Aviso: la regla en edición se usa en varias estrategias -->
    <div v-if="editUsageCount > 1" class="white flex-shrink-0">
      <v-alert
        dense
        text
        type="warning"
        icon="mdi-alert-outline"
        class="ma-0 rounded-0 text-body-2"
      >
        {{
          $t("rule_edit_used_in_strategies_warning", { count: editUsageCount })
        }}
      </v-alert>
    </div>

    <!-- ════════════ LIENZO ════════════ -->
    <div
      class="canvas-viewport position-relative flex-grow-1 overflow-hidden"
      ref="viewport"
      :class="{ 'is-panning': panning }"
      @mousedown="startPan"
      @wheel.prevent="onWheel"
    >
      <div
        class="canvas-content d-inline-flex align-start position-absolute"
        ref="content"
        :style="{
          transform:
            'translate(' + pan.x + 'px,' + pan.y + 'px) scale(' + zoom + ')'
        }"
      >
        <!-- SVG de conectores (coordenadas en espacio de contenido, sin escalar) -->
        <svg
          class="connectors position-absolute overflow-visible"
          :width="svgSize.w"
          :height="svgSize.h"
        >
          <path
            v-for="(p, i) in connectorPaths"
            :key="i"
            :d="p.d"
            fill="none"
            :stroke="p.color"
            stroke-width="2"
            stroke-linecap="round"
          />
        </svg>

        <!-- ── Disparador (Evaluada) ── -->
        <div class="node-anchor d-flex align-center overflow-visible">
          <div class="node-card is-trigger rounded-lg white" ref="trigger">
            <div class="d-flex align-center pa-3" style="gap: 10px;">
              <v-sheet
                rounded="lg"
                color="grey lighten-3"
                height="30"
                width="30"
                class="d-flex align-center justify-center"
              >
                <v-icon size="18" color="grey darken-1">
                  {{
                    isGuardrail
                      ? "mdi-calculator-variant-outline"
                      : "mdi-clock-outline"
                  }}
                </v-icon>
              </v-sheet>
              <div class="d-flex flex-column" style="gap: 3px;">
                <div
                  class="text-caption text-uppercase line-height-1 grey--text font-weight-medium"
                >
                  {{ $t("Evaluated") }}
                </div>
                <div class="text-body-2 font-weight-bold line-height-1">
                  {{
                    isGuardrail
                      ? $t("guardrail_trigger_title")
                      : freqLabel(rule.schedule && rule.schedule.frequency)
                  }}
                </div>
              </div>
            </div>
            <v-divider class="grey lighten-3" />
            <!-- Un guardarail no tiene horario propio: se evalúa siempre
                 después de calcular el precio, así que el nodo es fijo. -->
            <div v-if="isGuardrail" class="pa-3 text-caption grey--text">
              {{ $t("guardrail_trigger_subtitle") }}
            </div>
            <div v-else class="pa-3">
              <a-pricing-strategy-schedule-editor
                v-model="rule.schedule"
                @input="scheduleConnectors"
              />
            </div>
          </div>
        </div>

        <!-- ── Rutas (condiciones) ── -->
        <div class="node-anchor d-flex align-center overflow-visible">
          <div
            class="d-flex flex-column align-start position-relative"
            ref="routesCol"
            style="gap: 40px;"
          >
            <div
              v-for="route in rule.routes"
              :key="route.id"
              class="d-flex align-center position-relative"
              :ref="'route-' + route.id"
            >
              <!-- ruta vacía -->
              <div
                v-if="route.conditions.length === 0"
                class="empty-route d-flex flex-column align-center justify-center rounded-lg text-caption grey--text cursor-pointer"
                @click="openDrawer(route.id, 0)"
              >
                <v-icon size="22">mdi-plus-circle-outline</v-icon>
                <span class="text-caption">{{ $t("add_condition") }}</span>
              </div>

              <!-- condiciones de la ruta (AND) -->
              <div
                v-for="(cond, ci) in route.conditions"
                :key="cond.id"
                class="d-flex align-center"
              >
                <div
                  v-if="ci > 0"
                  class="and-link d-flex align-center justify-center flex-shrink-0"
                >
                  <div class="and-line"></div>
                  <div
                    class="and-ball rounded-circle d-flex align-center justify-center prevent-select"
                  >
                    {{ $t("and").toUpperCase() }}
                  </div>
                </div>

                <div class="node-card is-condition rounded-lg white">
                  <!-- cabecera con separador -->
                  <div class="d-flex align-center justify-space-between pa-3">
                    <div class="d-flex align-center" style="gap: 10px;">
                      <v-sheet
                        :color="typeColor(cond.rule_type_code) + '1a'"
                        rounded="lg"
                        height="30"
                        width="30"
                        class="d-flex align-center justify-center"
                      >
                        <v-icon
                          size="18"
                          :color="typeColor(cond.rule_type_code)"
                        >
                          {{ typeDef(cond.rule_type_code).icon }}
                        </v-icon>
                      </v-sheet>
                      <div class="text-body-2 font-weight-bold">
                        {{ typeDef(cond.rule_type_code).title }}
                      </div>
                    </div>
                    <!-- El guardarail fijo solo admite cambios de valor -->
                    <div
                      v-if="!isStructureLocked"
                      class="d-flex align-center"
                      style="gap: 2px;"
                    >
                      <v-tooltip bottom>
                        <template v-slot:activator="{ on }">
                          <v-btn
                            x-small
                            icon
                            v-on="on"
                            @click.stop="openReplaceDrawer(route.id, cond.id)"
                          >
                            <v-icon>mdi-swap-horizontal</v-icon>
                          </v-btn>
                        </template>
                        <span>{{ $t("replace_condition") }}</span>
                      </v-tooltip>
                      <v-tooltip bottom>
                        <template v-slot:activator="{ on }">
                          <v-btn
                            :disabled="route.conditions.length <= 1"
                            x-small
                            icon
                            v-on="on"
                            @click.stop="
                              route.conditions.length > 1 &&
                                askRemoveCondition(route.id, cond.id)
                            "
                          >
                            <v-icon>mdi-trash-can-outline</v-icon>
                          </v-btn>
                        </template>
                        <span>{{ $t("delete_condition") }}</span>
                      </v-tooltip>
                    </div>
                  </div>

                  <v-divider class="grey lighten-3" />

                  <!-- cuerpo dinámico por tipo — añadir un tipo nuevo: crear aPricingStrategyRuleTypeConditionBody-<code>.vue -->
                  <component
                    :is="condBodyComponent(cond.rule_type_code)"
                    :cond="cond"
                    :rule-type="typeDef(cond.rule_type_code)"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- ── Acción (Resultado) ── -->
        <div class="node-anchor d-flex align-center overflow-visible">
          <div
            class="node-card is-action rounded-lg white"
            ref="action"
            :class="{ 'is-per-route': perRouteAction }"
            :style="{ transform: 'translateY(' + actionShiftY + 'px)' }"
          >
            <!-- ── Cabecera: título + modo (ambos globales a la regla) ── -->
            <div ref="actionHead">
              <div class="d-flex align-center pa-3" style="gap: 10px;">
                <v-sheet
                  rounded="lg"
                  :style="{ background: 'var(--rc-primary-bg)' }"
                  height="30"
                  width="30"
                  class="d-flex align-center justify-center"
                >
                  <v-icon size="18" color="primary">
                    mdi-lightning-bolt
                  </v-icon>
                </v-sheet>
                <div class="d-flex flex-column" style="gap: 3px;">
                  <div
                    class="text-caption text-uppercase line-height-1 primary--text font-weight-medium"
                  >
                    {{ $t("action") }}
                  </div>
                  <div class="text-body-2 font-weight-bold line-height-1">
                    {{ $t("result") }}
                  </div>
                </div>
              </div>
              <v-divider class="grey lighten-3" />
              <!-- El modo es de la regla, así que va en la cabecera y no
                   dentro del bloque de slots. -->
              <div v-if="canChooseActionMode" class="pa-3">
                <v-btn-toggle
                  v-model="rule.action.mode"
                  mandatory
                  rounded
                  class="d-flex action-mode-toggle"
                >
                  <v-btn
                    v-for="m in availableActionModes"
                    :key="m"
                    :value="m"
                    height="30"
                    x-small
                    class="font-weight-bold flex-grow-1"
                    :class="{ 'primary--text': rule.action.mode === m }"
                  >
                    {{ actionModeLabel(m) }}
                  </v-btn>
                </v-btn-toggle>
              </div>

              <!-- Un valor para toda la regla o uno por ruta. Solo con dos
                   rutas o más: con una no hay nada que distinguir.

                   Switch y no otro v-btn-toggle: apilado bajo el de modo, dos
                   barras iguales compiten, y de las dos decisiones solo el modo
                   es la principal. Un sí/no se lee como lo que es. -->
              <div
                v-if="canChooseValueScope"
                class="d-flex align-center justify-space-between px-3 pb-3"
              >
                <span
                  class="text-caption text-uppercase font-weight-medium grey--text"
                >
                  {{ $t("action_value_scope_per_route") }}
                </span>
                <v-switch
                  :input-value="perRouteAction"
                  dense
                  inset
                  hide-details
                  class="ma-0 pa-0 action-scope-switch"
                  @change="applyValueScope"
                />
              </div>

              <!-- La unidad de `manual` es de la regla, no de la ruta, así que
                   con valor por ruta sube aquí. Con valor único se queda abajo,
                   emparejada con la etiqueta de subir/bajar. -->
              <div
                v-if="perRouteAction && rule.action.mode === 'manual'"
                class="d-flex align-center justify-space-between px-3 pb-3"
              >
                <span
                  class="text-caption text-uppercase font-weight-medium grey--text"
                >
                  {{ $t("adjust_price") }}
                </span>
                <v-btn-toggle
                  v-model="rule.action.unit"
                  mandatory
                  dense
                  rounded
                >
                  <v-btn x-small value="fixed" class="font-weight-bold">
                    €
                  </v-btn>
                  <v-btn x-small value="percentage" class="font-weight-bold">
                    %
                  </v-btn>
                </v-btn-toggle>
              </div>
            </div>

            <!-- ── Un valor por ruta, a la altura de su ruta ── -->
            <div
              v-if="perRouteAction"
              class="action-slots position-relative"
              :style="{ height: slotLayout.height + 'px' }"
            >
              <div
                v-for="item in slotLayout.items"
                :key="item.routeId"
                :ref="'action-slot-' + item.routeId"
                class="action-slot position-absolute d-flex align-center px-3"
                :style="{ top: item.top + 'px', height: item.height + 'px' }"
              >
                <div class="action-slot-inner flex-grow-1">
                  <div
                    class="action-slot-badge text-caption text-uppercase font-weight-medium grey--text"
                  >
                    {{ slotFieldLabel(item.routeId) }}
                  </div>

                  <a-pricing-strategy-rule-type-condition-field-amount-slider
                    v-if="rule.action.mode === 'manual'"
                    :value="routeAction(item.routeId).manual.amount"
                    :min="actionAmountMin"
                    :max="actionAmountMax"
                    :step="actionAmountStep"
                    :unit="amountUnitSymbol"
                    @input="
                      setRouteActionField(
                        item.routeId,
                        'manual',
                        'amount',
                        $event
                      )
                    "
                  />

                  <a-pricing-strategy-rule-type-condition-field-number
                    v-else-if="rule.action.mode === 'set_value'"
                    :value="routeAction(item.routeId).set_value.value"
                    :min="setValueBounds.min"
                    :max="setValueBounds.max"
                    :step="setValueBounds.step"
                    unit="€"
                    @input="
                      setRouteActionField(
                        item.routeId,
                        'set_value',
                        'value',
                        $event
                      )
                    "
                  />

                  <a-pricing-strategy-rule-type-condition-field-compset
                    v-else-if="rule.action.mode === 'market'"
                    :value="routeAction(item.routeId).market.compset_range"
                    :values="compsetOptions"
                    @input="
                      setRouteActionField(
                        item.routeId,
                        'market',
                        'compset_range',
                        $event
                      )
                    "
                  />

                  <!-- prioridad del aviso: una por ruta. El rótulo lo pone ya
                       la cabecera del slot. -->
                  <v-btn-toggle
                    v-else-if="rule.action.mode === 'notify'"
                    :value="routeAction(item.routeId).notify.priority"
                    mandatory
                    dense
                    rounded
                    class="d-flex action-toggle-fill"
                    @change="
                      setRouteActionField(
                        item.routeId,
                        'notify',
                        'priority',
                        $event
                      )
                    "
                  >
                    <v-btn
                      v-for="p in notifyPriorities"
                      :key="p"
                      :value="p"
                      x-small
                      class="font-weight-bold"
                    >
                      {{ $t("guardrail_priority_" + p) }}
                    </v-btn>
                  </v-btn-toggle>
                </div>
              </div>
            </div>

            <!-- ── Valor único ──
                 Escribe en el bucket de la primera ruta, igual que los slots
                 en el suyo: el valor vive siempre en las rutas y el botón solo
                 decide cómo se pinta y cómo se serializa.

                 v-if y no v-else: entre medias va la nota del aviso, que
                 rompería el emparejamiento con el v-if de los slots. -->
            <div
              v-if="!perRouteAction"
              class="d-flex flex-column pa-3 pt-0"
              style="gap: 20px;"
            >
              <!-- modo manual -->
              <div
                v-show="rule.action.mode === 'manual'"
                style="display: flex; flex-direction: column; gap: 20px;"
              >
                <div class="d-flex align-center justify-space-between">
                  <span
                    class="text-caption text-uppercase font-weight-medium grey--text"
                  >
                    {{ amountDirectionLabel }}
                  </span>
                  <v-btn-toggle
                    v-model="rule.action.unit"
                    mandatory
                    dense
                    rounded
                  >
                    <v-btn x-small value="fixed" class="font-weight-bold">
                      €
                    </v-btn>
                    <v-btn x-small value="percentage" class="font-weight-bold">
                      %
                    </v-btn>
                  </v-btn-toggle>
                </div>
                <a-pricing-strategy-rule-type-condition-field-amount-slider
                  :value="singleValueAmount"
                  :min="actionAmountMin"
                  :max="actionAmountMax"
                  :step="actionAmountStep"
                  :unit="amountUnitSymbol"
                  @input="
                    setRouteActionField(
                      singleValueRouteId,
                      'manual',
                      'amount',
                      $event
                    )
                  "
                />
              </div>

              <!-- modo mercado.
                   v-if y no v-show: la raíz del componente lleva d-flex, que
                   es display:flex !important y ganaría al display:none. -->
              <a-pricing-strategy-rule-type-condition-field-compset
                v-if="rule.action.mode === 'market'"
                :value="singleValueAction.market.compset_range"
                :values="compsetOptions"
                :label="$t('compset_position')"
                @input="
                  setRouteActionField(
                    singleValueRouteId,
                    'market',
                    'compset_range',
                    $event
                  )
                "
              />

              <!-- modo fijar precio -->
              <a-pricing-strategy-rule-type-condition-field-number
                v-if="rule.action.mode === 'set_value'"
                :value="singleValueAction.set_value.value"
                :min="setValueBounds.min"
                :max="setValueBounds.max"
                :step="setValueBounds.step"
                unit="€"
                :label="$t('adjust_price')"
                @input="
                  setRouteActionField(
                    singleValueRouteId,
                    'set_value',
                    'value',
                    $event
                  )
                "
              />

              <!-- modo aviso -->
              <div
                v-if="rule.action.mode === 'notify'"
                class="d-flex flex-column"
                style="gap: 5px;"
              >
                <span
                  class="text-caption text-uppercase font-weight-medium grey--text"
                >
                  {{ $t("guardrail_notify_priority") }}
                </span>
                <v-btn-toggle
                  :value="singleValueAction.notify.priority"
                  mandatory
                  dense
                  rounded
                  class="d-flex action-toggle-fill"
                  @change="
                    setRouteActionField(
                      singleValueRouteId,
                      'notify',
                      'priority',
                      $event
                    )
                  "
                >
                  <v-btn
                    v-for="p in notifyPriorities"
                    :key="p"
                    :value="p"
                    x-small
                    class="font-weight-bold"
                  >
                    {{ $t("guardrail_priority_" + p) }}
                  </v-btn>
                </v-btn-toggle>
              </div>
            </div>

            <!-- ── Pie: nota del aviso + categorías afectadas ──
                 La nota va aquí dentro, no suelta encima: actionShiftY alinea
                 los slots con las rutas usando el alto de este pie, y todo lo
                 que quede por debajo de los slots sin contarse desplaza el
                 bloque y curva los conectores. -->
            <div ref="actionFoot">
              <div
                v-if="rule.action.mode === 'notify'"
                class="text-caption grey--text px-3 pb-3"
              >
                {{ $t("guardrail_notify_hint") }}
              </div>
              <v-divider />
              <div class="d-flex flex-column pa-3" style="gap: 12px;">
                <div
                  class="text-caption text-uppercase font-weight-medium grey--text"
                >
                  {{ $t("affected_categories") }}
                </div>
                <div class="d-flex flex-wrap" style="gap: 6px;">
                  <v-chip
                    v-for="cat in categoriesWithAll"
                    :key="cat.id"
                    small
                    :color="isCatActive(cat.id) ? 'primary' : ''"
                    :dark="isCatActive(cat.id)"
                    :outlined="!isCatActive(cat.id)"
                    @click="toggleCat(cat.id)"
                    class="font-weight-medium"
                  >
                    {{ cat.name }}
                  </v-chip>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- nodos "+" sobre las curvas (dentro del contenido → se mueven y escalan
             con el mismo transform que tarjetas y líneas, sin desfase al zoom/pan) -->
        <button
          v-for="(n, i) in visiblePlusNodes"
          :key="'plus-' + i"
          class="plus-node rounded-circle d-flex align-center justify-center cursor-pointer"
          :style="{ left: n.x + 'px', top: n.y + 'px' }"
          @click="openDrawer(n.routeId, n.index)"
          :title="$t('add_condition')"
        >
          <v-icon size="16">mdi-plus</v-icon>
        </button>

        <!-- nodos "−" al inicio de cada ruta: borran la ruta entera -->
        <v-tooltip
          bottom
          v-for="(n, i) in visibleMinusNodes"
          :key="'minus-' + i"
        >
          <template v-slot:activator="{ on }">
            <button
              class="minus-node rounded-circle d-flex align-center justify-center cursor-pointer"
              :style="{ left: n.x + 'px', top: n.y + 'px' }"
              v-on="on"
              @click="askRemoveRoute(n.routeId)"
            >
              <v-icon size="16">mdi-minus</v-icon>
            </button>
          </template>
          <span>{{ $t("delete_route") }}</span>
        </v-tooltip>

        <!-- botón nueva ruta: debajo de todas las rutas, centrado en el diagrama -->
        <div
          v-if="
            !isStructureLocked &&
              rule.routes.length < maxRoutes &&
              newRouteBtnPos
          "
          class="new-route-anchor"
          :style="{
            left: newRouteBtnPos.x + 'px',
            top: newRouteBtnPos.y + 'px'
          }"
          ref="newRouteBtn"
        >
          <v-btn
            outlined
            small
            rounded
            color="secondary"
            class="text-none new-route-btn"
            @click="addRoute"
          >
            <v-icon left size="15">mdi-source-branch</v-icon>
            {{ $t("new_or_route") }}
          </v-btn>
        </div>
      </div>
      <!-- /canvas-content -->

      <!-- zoom -->
      <div class="zoom-bar d-flex align-center white rounded-lg pa-1 px-2">
        <v-btn icon x-small @click="zoomBy(-0.1)">
          <v-icon size="18">mdi-minus</v-icon>
        </v-btn>
        <span
          class="zoom-val text-caption font-weight-bold text-center flex-shrink-0"
          >{{ Math.round(zoom * 100) }}%</span
        >
        <v-btn icon x-small @click="zoomBy(0.1)">
          <v-icon size="18">mdi-plus</v-icon>
        </v-btn>
        <v-btn icon x-small @click="fitToView">
          <v-icon size="17">mdi-fit-to-page-outline</v-icon>
        </v-btn>
      </div>
    </div>

    <!-- ════════════ PANEL DE CONDICIONES (absoluto dentro del canvas) ════════════ -->
    <transition name="drawer-slide">
      <div
        v-if="drawer"
        class="drawer-overlay position-absolute d-flex justify-end"
        @click.self="drawer = false"
      >
        <div
          class="drawer-panel fill-height d-flex flex-column overflow-hidden white elevation-8"
        >
          <div class="drawer-head pa-4 pb-2">
            <div class="d-flex align-center justify-space-between mb-1">
              <div>
                <div class="text-h6 font-weight-bold">
                  {{
                    drawerIsReplace
                      ? $t("replace_condition")
                      : $t("add_condition")
                  }}
                </div>
                <div class="text--secondary caption">
                  {{ $t("select_trigger") }}
                </div>
              </div>
              <v-btn icon small @click="drawer = false">
                <v-icon>mdi-close</v-icon>
              </v-btn>
            </div>
            <v-text-field
              v-model="search"
              :placeholder="$t('search') + '...'"
              prepend-inner-icon="mdi-magnify"
              rounded
              outlined
              dense
              clearable
              hide-details
              class="mt-2"
              color="secondary"
            ></v-text-field>
          </div>
          <div class="drawer-body d-flex flex-grow-1 overflow-hidden">
            <div class="cat-side overflow-y-auto flex-shrink-0">
              <div
                v-for="g in groups"
                :key="g.id"
                class="cs-item d-flex align-center py-3 px-3 cursor-pointer"
                :class="{ active: activeGroup === g.id }"
                style="gap: 10px;"
                @click="activeGroup = g.id"
              >
                <v-icon size="18" class="cs-icon flex-shrink-0">
                  {{ g.icon }}
                </v-icon>
                <div class="cs-name text-body-2 line-height-1 text-capitalize">
                  {{ $t(g.name) }}
                </div>
              </div>
            </div>
            <div class="overflow-y-auto pa-2 flex-grow-1">
              <v-card
                v-for="t in filteredTriggers"
                :key="t.code"
                elevation="0"
                rounded="lg"
                outlined
                class="trigger-item mb-2 pa-2 cursor-pointer"
                @click="pickTrigger(t.code)"
              >
                <div class="d-flex" style="gap: 10px;">
                  <v-sheet
                    rounded="lg"
                    :color="typeColor(t.code) + '1a'"
                    height="30"
                    width="30"
                    class="d-flex align-center justify-center flex-shrink-0"
                  >
                    <v-icon size="18" :color="typeColor(t.code)">
                      {{ t.icon }}
                    </v-icon>
                  </v-sheet>
                  <div class="d-flex flex-column" style="gap: 3px;">
                    <div class="text-body-2 font-weight-bold">
                      {{ t.title }}
                    </div>
                    <div class="text-caption grey--text line-height-1">
                      {{ t.subtitle }}
                    </div>
                  </div>
                </div>
              </v-card>
              <div
                v-if="filteredTriggers.length === 0"
                class="no-triggers grey--text text--lighten-1 pa-4 text-center caption"
              >
                {{ $t("No_rules_found") }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </transition>

    <!-- Confirmación de borrado de condición -->
    <v-dialog
      v-model="confirmDelete.show"
      :fullscreen="$vuetify.breakpoint.xsOnly"
      max-width="400"
    >
      <v-card>
        <v-card-title center class="headline justify-center">
          {{ $t("are_you_sure") }}
        </v-card-title>
        <v-card-text class="text-center justify-center">
          <p>
            {{
              confirmDelete.kind === "route"
                ? $t("sure_delete_route")
                : $t("sure_delete_pricing_strategy_rule")
            }}
          </p>
        </v-card-text>
        <v-card-actions class="ps-5 pb-5 justify-center">
          <v-btn
            color="red"
            rounded
            depressed
            text
            class="pl-5 pr-5"
            @click="confirmRemove"
          >
            {{ $t("delete") }}
          </v-btn>
          <v-btn
            color=""
            text
            rounded
            depressed
            class="pl-5 pr-5"
            @click="confirmDelete.show = false"
          >
            {{ $t("cancel") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar
      v-model="snackbar.show"
      :color="snackbar.color"
      :timeout="2600"
      rounded="pill"
      bottom
    >
      {{ snackbar.text }}
    </v-snackbar>

    <!-- ════════════ BARRA DE RESUMEN ════════════ -->
    <div
      class="rule-summary-bar flex-shrink-0 overflow-hidden d-flex align-center justify-center px-4"
      style="gap: 12px;"
    >
      <div
        class="rsb-check rounded d-flex align-center justify-center flex-shrink-0"
      >
        <v-icon size="14" color="success">mdi-check</v-icon>
      </div>
      <div class="rsb-text text-caption text-truncate line-height-1">
        <span
          v-for="(token, ti) in ruleSummaryTokens"
          :key="ti"
          :class="'rsb-' + token.type"
          >{{ token.text }}</span
        >
      </div>
    </div>

    <!-- ════════════ RESULTADO DE LA PRUEBA ════════════ -->
    <!-- Misma envoltura que el detalle de regla de Recomendaciones, para que
         se lea igual en los dos sitios -->
    <v-dialog
      v-model="test_dialog"
      :fullscreen="$vuetify.breakpoint.xsOnly"
      max-width="600"
    >
      <v-card v-if="testOutcome">
        <v-card-title class="headline justify-space-between">
          <div class="d-flex flex-column">
            <span class="font-weight-bold">
              {{ rule.name || $t("rule") }}
            </span>
            <div class="d-flex align-center text-body-2" style="gap: 5px">
              <v-icon
                size="16"
                :color="testOutcome.triggered ? 'success' : 'grey darken-1'"
              >
                {{
                  testOutcome.triggered
                    ? "mdi-check-circle-outline"
                    : "mdi-close-circle-outline"
                }}
              </v-icon>
              <span
                :class="
                  testOutcome.triggered
                    ? 'success--text'
                    : 'grey--text text--darken-1'
                "
              >
                {{
                  testOutcome.triggered
                    ? $t("test_rule_triggered")
                    : $t("test_rule_not_triggered")
                }}
              </span>
              <span class="grey--text">{{ formattedTestDate }}</span>
            </div>
          </div>
          <v-btn icon @click="test_dialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text class="mt-2">
          <!-- Una regla que no salta no tiene medida, pero sí rutas evaluadas:
               el detalle enseña por qué ninguna encajó -->
          <div
            v-if="!testOutcome.triggered && testOutcome.entry.reason"
            class="text-body-2 grey--text text--darken-1 mb-3"
          >
            {{ testOutcome.entry.reason }}
          </div>
          <a-pricing-strategy-rule-detail
            :rule="testRuleForDetail"
            :suggestion="testOutcome.entry"
          />
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
/* ════════════════════════════════════════════════════════════════════════════
   aPricingStrategyRuleCanvas.vue  —  Vue 2.6 + Vuetify 2.3 (hawg-pwa / Veetal)

   Lienzo para crear/editar una regla de precios:
     [Evaluada] → (condiciones · Y dentro de fila / O entre filas) → [Acción]

   INTEGRACIÓN CON EL STORE (autónoma):
   · Lee state.pricingstrategy.rule_to_edit en created() para construir el modelo.
   · onSave() despacha addPricingStrategyRule / setPricingStrategyRule y cierra.
   · onCancel() cierra el dialog via store.
   · rule_types        → state.pricingstrategy.rule_types
   · paleta de colores → state.chain.chain.colors
   · categorías        → state.pricingstrategy.strategy_room_types

   UI por tipo de regla (título/subtítulo/color/grupo del catálogo): RULE_TYPE_UI.
   ═══════════════════════════════════════════════════════════════════════════ */

import Vue from "vue";
import aColorPalette from "@/components/aColorPalette.vue";
import aPricingStrategyRuleDetail from "@/components/pricing-strategy/aPricingStrategyRuleDetail.vue";
import aPricingStrategyScheduleEditor from "@/components/pricing-strategy/aPricingStrategyScheduleEditor.vue";
import {
  actionPhrase,
  effectiveActionFor,
  hasPerRouteAction,
  isNeutralRuleColor,
  randomRuleColor
} from "@/components/pricing-strategy/pricingStrategyUtils";
import * as Utils from "@/utils.js";
import aPricingStrategyRuleTypeConditionFieldCompset from "./rule-types-conditions/fields/aPricingStrategyRuleTypeConditionFieldCompset.vue";
import aPricingStrategyRuleTypeConditionFieldNumber from "./rule-types-conditions/fields/aPricingStrategyRuleTypeConditionFieldNumber.vue";
import aPricingStrategyRuleTypeConditionFieldAmountSlider from "./rule-types-conditions/fields/aPricingStrategyRuleTypeConditionFieldAmountSlider.vue";
import {
  DEFAULT_CURRENCY_AMOUNT,
  actionMode,
  actionModesFor,
  conditionComponent,
  conditionSummaryFields,
  defaultSettings as registryDefaultSettings
} from "./rule-types-conditions/ruleTypeRegistry.js";

// Espejo de --rc-* en <style>: SVG paths no pueden leer CSS vars.
// Mantener en sync con --rc-primary y --rc-connector del bloque <style>.
const RC_PRIMARY = "#2e21bd";

// Precio de la primera condición de una ruta. Se usa para espejar el guardarail
// fijo, donde ese número y el valor de la acción son la misma cosa.
function lockedRoutePrice(route) {
  const condition = ((route && route.conditions) || [])[0];
  const price = condition && condition.settings && condition.settings.price;
  return price == null ? null : price;
}

// El modo `clamp` se retiró al pasar a valores por ruta. Solo queda el alias
// por si algún draft de localStorage lo trae.
const GUARDRAIL_MODE_ALIASES = { clamp: "set_value" };
const RC_CONNECTOR_STROKE = "#c7ccd6"; // == --rc-connector

export default {
  name: "a-pricing-strategy-rule-canvas",
  components: {
    aColorPalette,
    aPricingStrategyRuleDetail,
    aPricingStrategyScheduleEditor,
    aPricingStrategyRuleTypeConditionFieldCompset,
    aPricingStrategyRuleTypeConditionFieldNumber,
    aPricingStrategyRuleTypeConditionFieldAmountSlider
  },

  // initialRule: regla existente a editar (opcional). draftKey: si se indica,
  // guarda un borrador en localStorage con esa clave.
  // maxRoutes/maxConditions: limites para el modo 1-ruta/1-condicion actual.
  // Cuando el backend soporte multi-ruta, pasar Infinity o no pasar.
  props: {
    initialRule: { type: Object, default: null },
    draftKey: { type: String, default: "" },
    maxRoutes: { type: Number, default: Infinity },
    maxConditions: { type: Number, default: Infinity },
    // "rule" | "guardrail". En modo guardarail el disparador es fijo (se
    // evalúa tras calcular el precio), el catálogo de condiciones se limita a
    // los tipos admitidos y la acción tiene un valor por ruta.
    mode: { type: String, default: "rule" }
  },

  data() {
    return {
      // ── Probar la regla (sólo entornos de desarrollo) ──────────────────
      test_menu: false,
      testing_rule: false,
      test_target_date: null,
      test_result: null,
      test_dialog: false,

      // El created() sobreescribe `rule` con datos del store (rule_to_edit).
      // Este valor inicial solo aplica en uso standalone sin store.
      rule: this.initialRule
        ? JSON.parse(JSON.stringify(this.initialRule))
        : {
            name: "",
            color: RC_PRIMARY,
            schedule: {
              frequency: "daily",
              hourly: { ranges: [{ from: "08:00", to: "20:00" }] },
              daily: { ranges: [{ from: "08:00", to: "20:00" }] },
              weekly: { weekday: 1, time: "09:00" },
              monthly: { day_of_month: 1, time: "09:00" }
            },
            routes: [{ id: 1, conditions: [] }],
            action: {
              mode: "manual",
              amount: 0,
              unit: "fixed",
              compset_range: "middle",
              per_route: false,
              room_type_ids: []
            }
          },

      // lienzo
      zoom: 1,
      pan: { x: 0, y: 0 },
      panning: false,
      panStart: { x: 0, y: 0, px: 0, py: 0 },

      // alineación de los slots de acción con las filas de ruta
      slotLayout: { height: 0, items: [] },
      actionShiftY: 0,

      // conectores
      connectorPaths: [],
      plusNodes: [],
      minusNodes: [],
      newRouteBtnPos: null,
      svgSize: { w: 10, h: 10 },

      // ui
      colorMenu: false,
      drawer: false,
      drawerTarget: { routeId: null, index: 0 },
      search: "",
      activeGroup: "",
      snackbar: { show: false, text: "", color: "primary" },
      confirmDelete: {
        show: false,
        kind: "condition",
        routeId: null,
        condId: null
      },

      seq: 1000,

      compsetBarHeights: [5, 8, 12, 15, 18]
    };
  },

  computed: {
    // Nº de estrategias que usan la regla en edición (0 si es una regla nueva)
    editUsageCount() {
      const ruleToEdit = this.$store.state.pricingstrategy.rule_to_edit;
      return ruleToEdit && ruleToEdit.id ? ruleToEdit.strategies_count || 0 : 0;
    },
    isGuardrail() {
      return this.mode === "guardrail";
    },
    // ── Datos del store (sin fallback, solo datos reales) ─────────────────
    ruleTypes() {
      const ps = this.$store.state.pricingstrategy;
      // Las condiciones son comunes a reglas y guardarailes: en modo regla se
      // ofrecen todas, y en modo guardarail solo las marcadas. Filtrar aquí
      // cubre de una vez el catálogo del drawer y los grupos del sidebar; si
      // solo se filtrara el primero, quedarían grupos vacíos.
      const types = (ps.rule_types || []).filter(rt =>
        this.isGuardrail ? rt.allowed_in_guardrails === true : true
      );
      return types.map(rt => {
        const titleKey = "pricing_strategy_rule_type_card_type_" + rt.code;
        const subtitleKey = "pricing_strategy_rule_type_" + rt.code;
        const groupNameKey = "pricing_strategy_rule_type_group_" + rt.group;
        return {
          code: rt.code,
          title: this.$te(titleKey) ? this.$t(titleKey) : rt.code,
          subtitle: this.$te(subtitleKey)
            ? this.$t(subtitleKey)
            : rt.description || "",
          icon: rt.icon || "mdi-tune",
          color: rt.color || "#1576c7",
          group: rt.group || "general",
          group_name: this.$te(groupNameKey)
            ? this.$t(groupNameKey)
            : rt.group || "General",
          group_icon: rt.group_icon || "mdi-tune",
          settings_schema: rt.settings_schema || {},
          action_modes: rt.action_modes
        };
      });
    },
    // Grupos derivados dinámicamente de los tipos de regla del backend
    groups() {
      const seen = new Set();
      return this.ruleTypes.reduce((acc, rt) => {
        if (!seen.has(rt.group)) {
          seen.add(rt.group);
          acc.push({ id: rt.group, name: rt.group_name, icon: rt.group_icon });
        }
        return acc;
      }, []);
    },
    categories() {
      const rts = this.$store.state.pricingstrategy.strategy_room_types || [];
      return rts.map(r => ({ id: r.id, name: r.name }));
    },
    categoriesWithAll() {
      return [{ id: "__all__", name: this.$t("all") }, ...this.categories];
    },

    // ── Items de selects ─────────────────────────────────────────────────
    freqItems() {
      const cfg = this.$store.state.pricingstrategy.strategy_config;
      const freqs = (cfg && cfg.evaluation && cfg.evaluation.frequencies) || [
        "hourly",
        "daily",
        "weekly",
        "monthly"
      ];
      return freqs.map(v => ({
        text: this.$t("evaluation_frequency_" + v),
        value: v
      }));
    },
    // ── Strategy config helpers ───────────────────────────────────────────
    strategyConfig() {
      return this.$store.state.pricingstrategy.strategy_config || null;
    },
    // Nombre a partir del contenido, para las reglas que se guardan sin él.
    //
    // Se compone con las mismas piezas que la barra de resumen —la primera
    // condición y su acción— pero recortado: un nombre es una etiqueta, no la
    // descripción entera. Si hay más rutas se dice cuántas, porque si no dos
    // reglas que empiezan igual acabarían llamándose igual.
    autoRuleName() {
      const routes = (this.rule.routes || []).filter(
        r => r.conditions && r.conditions.length > 0
      );
      if (!routes.length) return "";

      const route = routes[0];
      const part = this.summaryConditionParts(route.conditions[0]);
      if (!part || !part.title) return "";

      let name = part.title;
      if (
        part.value !== "" &&
        part.value !== null &&
        part.value !== undefined
      ) {
        name += ` ${part.value}${part.unit || ""}`;
      }
      if (part.spot) name += ` @ ${part.spot}`;

      const action = this.actionTokenText(
        this.perRouteAction ? route : this.rule.routes && this.rule.routes[0]
      );
      // "->" y no "→": rule.name es latin1 y la flecha se guarda como "?".
      // Es además la convención de los nombres que ya hay ("... -> +10€").
      if (action) name += ` -> ${action}`;

      if (routes.length > 1) {
        name += ` (+${routes.length - 1} ${this.$t("route").toLowerCase()})`;
      }

      return name.length > 80 ? name.slice(0, 79) + "…" : name;
    },
    // Hoy, en el formato del date-picker. No tiene sentido probar una regla
    // contra una fecha pasada: los datos de pickup ya no se mueven.
    today() {
      return new Date().toISOString().slice(0, 10);
    },
    // El mismo interruptor que el botón de sugerencias de prueba: probar una
    // regla llama al motor real, así que sólo en entornos de desarrollo.
    rule_test_enabled() {
      const cfg = this.strategyConfig;
      return !!(cfg && cfg.dev_tools && cfg.dev_tools.fake_suggestions_enabled);
    },
    // Fecha probada en el formato largo de la aplicación
    formattedTestDate() {
      return this.test_target_date
        ? Vue.moment(this.test_target_date).format("D MMM YYYY")
        : "";
    },
    // La regla tal como la evaluó el motor, para el detalle del resultado
    testRuleForDetail() {
      const rule = (this.test_result && this.test_result.rule) || {};
      return { ...rule, color: this.effectiveColor };
    },
    // Resultado de la prueba en la forma que consume el detalle de regla:
    // la sugerencia si ha saltado, y si no la entrada del trace, que explica
    // por qué no.
    testOutcome() {
      if (!this.test_result) return null;
      const suggestion = (this.test_result.suggestions || [])[0];
      if (suggestion) return { triggered: true, entry: suggestion };

      const trace = (this.test_result.trace || [])[0];
      return trace ? { triggered: false, entry: trace } : null;
    },
    // Defaults provenientes de strategy_config (como si vinieran de BD)
    defaultMode() {
      const cfg = this.strategyConfig;
      return (cfg && cfg.action && cfg.action.default_mode) || "manual";
    },
    defaultUnit() {
      const cfg = this.strategyConfig;
      return (
        (cfg &&
          cfg.action &&
          cfg.action.manual &&
          cfg.action.manual.default_unit) ||
        "fixed"
      );
    },
    defaultCompsetRange() {
      const cfg = this.strategyConfig;
      return (
        (cfg &&
          cfg.action &&
          cfg.action.market &&
          cfg.action.market.compset_range &&
          cfg.action.market.compset_range.default) ||
        "middle"
      );
    },
    defaultFrequency() {
      const cfg = this.strategyConfig;
      return (
        (cfg && cfg.evaluation && cfg.evaluation.default_frequency) || "daily"
      );
    },
    defaultGuardrailMode() {
      const cfg = this.guardrailConfig;
      return (cfg && cfg.action && cfg.action.default_mode) || "set_value";
    },
    actionAmountMin() {
      const cfg = this.strategyConfig;
      if (!cfg) return -50;
      const unit = this.rule.action.unit || "fixed";
      return (
        (cfg.action &&
          cfg.action.manual &&
          cfg.action.manual[unit] &&
          cfg.action.manual[unit].min) ||
        -50
      );
    },
    actionAmountMax() {
      const cfg = this.strategyConfig;
      if (!cfg) return 50;
      const unit = this.rule.action.unit || "fixed";
      return (
        (cfg.action &&
          cfg.action.manual &&
          cfg.action.manual[unit] &&
          cfg.action.manual[unit].max) ||
        50
      );
    },
    actionAmountStep() {
      const cfg = this.strategyConfig;
      if (!cfg) return 1;
      const unit = this.rule.action.unit || "fixed";
      return (
        (cfg.action &&
          cfg.action.manual &&
          cfg.action.manual[unit] &&
          cfg.action.manual[unit].step) ||
        1
      );
    },
    // ── Acción de guardarail ─────────────────────────────────────────────
    guardrailConfig() {
      return (this.strategyConfig && this.strategyConfig.guardrail) || null;
    },
    // Cotas del importe absoluto de la acción "fijar valor"
    setValueBounds() {
      const cfg = this.guardrailConfig;
      const sv = (cfg && cfg.action && cfg.action.set_value) || {};
      return {
        min: sv.min != null ? sv.min : 0,
        max: sv.max != null ? sv.max : 9999,
        step: sv.step || 1
      };
    },
    // Sin `default` en la config, un importe redondo y bajo: `null` dejaba el
    // campo vacío y el resumen del pie diciendo "fijar el precio en null€".
    defaultSetValue() {
      const cfg = this.guardrailConfig;
      const sv = (cfg && cfg.action && cfg.action.set_value) || {};
      return sv.default != null ? sv.default : DEFAULT_CURRENCY_AMOUNT;
    },
    notifyPriorities() {
      const cfg = this.guardrailConfig;
      const n = (cfg && cfg.action && cfg.action.notify) || {};
      return n.priorities || [1, 2, 3];
    },
    defaultNotifyPriority() {
      const cfg = this.guardrailConfig;
      const n = (cfg && cfg.action && cfg.action.notify) || {};
      return n.default_priority != null ? n.default_priority : 2;
    },
    // ¿La acción de esta regla lleva un valor distinto por ruta?
    //
    // Lo decide el usuario con el botón, no el modo: todos los modos admiten
    // valor por ruta. Con una sola ruta no hay nada que distinguir, así que se
    // cae solo a valor único — que es lo que hace que borrar rutas hasta dejar
    // una no necesite ningún código de limpieza.
    perRouteAction() {
      return (
        this.rule.action.per_route === true &&
        (this.rule.routes || []).length > 1
      );
    },
    // El guardarail fijo de precio mínimo/máximo no elige nada: es siempre
    // fijar un valor, y siempre uno por ruta (el suelo y el techo). Solo se le
    // editan los dos importes.
    canChooseActionMode() {
      return this.availableActionModes.length > 1 && !this.isStructureLocked;
    },
    // El switch solo tiene sentido con dos rutas o más.
    canChooseValueScope() {
      return (this.rule.routes || []).length > 1 && !this.isStructureLocked;
    },
    // Ruta cuyo valor se edita cuando la acción es única. Los controles de
    // valor único escriben en su bucket igual que los slots escriben en el
    // suyo: así los buckets son la única fuente de verdad del valor y cambiar
    // el botón no tiene que copiar nada de sitio.
    singleValueRouteId() {
      const routes = this.rule.routes || [];
      return routes.length ? routes[0].id : null;
    },
    // Los buckets de esa ruta, para enganchar los controles de valor único.
    singleValueAction() {
      return this.routeAction(this.singleValueRouteId);
    },
    singleValueAmount() {
      const amount = this.singleValueAction.manual.amount;
      return amount == null ? 0 : amount;
    },
    // Modos de acción ofrecidos en el toggle.
    //
    // En reglas normales se mantiene el comportamiento de siempre: los modos
    // de la configuración del hotel (manual / mercado), sin filtrar por tipo
    // de condición — el usuario puede elegir cualquiera.
    //
    // En guardarailes sí se filtra por lo que declara el tipo: un límite de
    // precio no admite recolocarse en el compset, por ejemplo.
    availableActionModes() {
      if (!this.isGuardrail) {
        const modes = (this.strategyConfig &&
          this.strategyConfig.action &&
          this.strategyConfig.action.modes) || ["manual", "market"];
        // `notify` se ofrece siempre mientras /get-strategy-config no lo
        // declare en action.modes.
        return modes.indexOf("notify") > -1 ? modes : modes.concat("notify");
      }
      const condition = this.firstCondition;
      const type = condition ? this.typeDef(condition.rule_type_code) : null;
      const declared = type ? actionModesFor(type) : ["set_value"];
      const allowed = (this.guardrailConfig &&
        this.guardrailConfig.action &&
        this.guardrailConfig.action.modes) || ["set_value", "market", "notify"];
      const modes = declared.filter(m => allowed.indexOf(m) >= 0);
      return modes.length ? modes : declared;
    },
    // Rótulo del slot de acción: el mismo que lleva el campo cuando la acción
    // es única. Se repite igual en todas las rutas a propósito — dice QUÉ se
    // ajusta, que es lo útil; de qué ruta es ya se ve a la izquierda.
    actionFieldLabel() {
      const labels = {
        manual: "adjust_price",
        set_value: "adjust_price",
        market: "compset_position",
        notify: "guardrail_notify_priority"
      };
      const key = labels[this.rule.action.mode];
      return key ? this.$t(key) : "";
    },
    // El guardarail fijo de precio mínimo/máximo. Se le editan los valores,
    // pero no la estructura: ni rutas ni condiciones.
    isStructureLocked() {
      const ruleToEdit = this.$store.state.pricingstrategy.rule_to_edit;
      return !!(ruleToEdit && ruleToEdit.locked);
    },
    // Rutas cuyo precio de condición y valor de acción son EL MISMO número.
    // Es el único sitio que decide si toca espejar; el resto pregunta aquí.
    lockedValueRoutes() {
      if (!this.isStructureLocked) return [];
      if (this.rule.action.mode !== "set_value") return [];
      return this.rule.routes || [];
    },
    // Primitivo estable para poder observar los precios de las condiciones:
    // los cuerpos de condición mutan cond.settings in situ y no emiten nada,
    // así que no hay ningún evento al que engancharse.
    lockedConditionPrices() {
      return this.lockedValueRoutes
        .map(route => lockedRoutePrice(route))
        .join("|");
    },
    firstCondition() {
      const routes = this.rule.routes || [];
      const conditions = (routes[0] && routes[0].conditions) || [];
      return conditions[0] || null;
    },
    compsetOptions() {
      const cfg = this.strategyConfig;
      return (
        (cfg &&
          cfg.action &&
          cfg.action.market &&
          cfg.action.market.compset_range &&
          cfg.action.market.compset_range.options) || [
          "lowest",
          "low",
          "middle",
          "high",
          "highest"
        ]
      );
    },
    compsetItems() {
      const values = ["lowest", "low", "middle", "high", "highest"];
      const longPrefix = "pricing_strategy_rule_type_occupancy-compset_action_";
      const shortPrefix = "action_compset_pos_";
      return values.map((v, i) => ({
        text: this.$te(longPrefix + v) ? this.$t(longPrefix + v) : v,
        label: this.$te(shortPrefix + v) ? this.$t(shortPrefix + v) : v,
        value: v,
        barIndex: i
      }));
    },
    drawerIsReplace() {
      return this.drawerTarget.mode === "replace";
    },
    filteredTriggers() {
      const q = (this.search || "").toLowerCase();
      return this.ruleTypes.filter(t => {
        if (t.group !== this.activeGroup) return false;
        if (!q) return true;
        return (t.title + " " + t.subtitle).toLowerCase().indexOf(q) > -1;
      });
    },
    // Solo se usa con valor único, donde el importe es el de la primera ruta.
    amountDirectionLabel() {
      return this.amountDirectionLabelFor(this.singleValueAmount);
    },
    amountUnitSymbol() {
      return this.rule.action.unit === "fixed" ? "€" : "%";
    },
    effectiveColor() {
      const c = this.rule.color;
      // Rechazar colores de fondo o vacíos
      if (!c || c.toLowerCase() === "#f0f2f5") return RC_PRIMARY;
      return c;
    },
    // Filtra los plus-nodes segun el limite de condiciones por ruta
    visiblePlusNodes() {
      if (this.isStructureLocked) return [];
      return this.plusNodes.filter(n => {
        const route = this.rule.routes.find(r => r.id === n.routeId);
        if (!route) return true;
        return route.conditions.length < this.maxConditions;
      });
    },
    // La unica ruta no se puede borrar
    visibleMinusNodes() {
      if (this.isStructureLocked) return [];
      return this.rule.routes.length > 1 ? this.minusNodes : [];
    },

    // ── Resumen en lenguaje natural para el footer ────────────────────────
    // Returns a flat array of { type, text } tokens for the summary bar.
    // Types: freq | sep | empty | or | and | title | value | spot | action | cats
    ruleSummaryTokens() {
      const tokens = [];

      // Frecuencia (los guardarailes no tienen: se evalúan tras el cálculo)
      tokens.push({
        type: "freq",
        text: this.isGuardrail
          ? this.$t("guardrail_trigger_title").toUpperCase() + ":"
          : this.freqLabel(
              this.rule.schedule && this.rule.schedule.frequency
            ).toUpperCase() + ":"
      });
      tokens.push({
        type: "sep",
        text: " " + this.$t("if").toUpperCase() + " "
      });

      // Condiciones
      const routes = (this.rule.routes || []).filter(
        r => r.conditions && r.conditions.length > 0
      );

      if (routes.length === 0) {
        tokens.push({ type: "empty", text: this.$t("add_condition") });
      } else {
        routes.forEach((route, ri) => {
          if (ri > 0)
            tokens.push({
              type: "or",
              text: " " + this.$t("or").toUpperCase() + " "
            });
          route.conditions.forEach((cond, ci) => {
            if (ci > 0)
              tokens.push({
                type: "and",
                text: " " + this.$t("and").toUpperCase() + " "
              });
            const part = this.summaryConditionParts(cond);
            tokens.push({ type: "title", text: "[" + part.title + "]" });
            tokens.push({ type: "sep", text: " = " });
            tokens.push({ type: "value", text: part.value + part.unit });
            if (part.spot) {
              tokens.push({ type: "sep", text: " @ " });
              tokens.push({ type: "spot", text: part.spot });
            }
          });
          // Con valor por ruta la acción se emite dentro del bucle, pegada a
          // las condiciones que la disparan
          if (this.perRouteAction) {
            tokens.push({ type: "sep", text: " → " });
            tokens.push({ type: "action", text: this.actionTokenText(route) });
          }
        });
      }

      // Acción única: el valor vive igualmente en el bucket de la primera ruta
      if (!this.perRouteAction) {
        tokens.push({ type: "sep", text: " → " });
        tokens.push({
          type: "action",
          text: this.actionTokenText(this.rule.routes && this.rule.routes[0])
        });
      }

      // Categorias
      const ids = this.rule.action.room_type_ids || [];
      let catsText = "";
      if (ids.length === 0 || ids.length >= this.categories.length) {
        catsText = this.$t("all");
      } else {
        catsText = ids
          .map(id => {
            const cat = this.categories.find(c => c.id === id);
            return cat ? cat.name : id;
          })
          .join(", ");
      }
      tokens.push({ type: "sep", text: " | " });
      tokens.push({ type: "cats", text: "[" + catsText + "]" });

      return tokens;
    }
  },

  watch: {
    rule: {
      deep: true,
      handler() {
        this.persist();
        this.scheduleConnectors();
      }
    },
    zoom() {
      this.scheduleConnectors();
    },
    // Cambiar de valor único a por ruta (o al revés) cambia el alto de la
    // tarjeta de acción. Se vacía la firma para forzar un remedido: si no, una
    // secuencia apagar → tocar rutas → encender puede reaprovechar una firma
    // rancia pero válida y dejar los slots desalineados.
    perRouteAction() {
      this._slotSig = "";
      this.$nextTick(() => {
        this.measureRouteSlots();
        this.measureActionFrame();
        this.scheduleConnectors();
      });
    },
    // Ver "Guardarail fijo" en methods. Sin `immediate`: al abrir, condición y
    // acción pueden venir legítimamente distintas del backend y pisar una con
    // otra en silencio sería peor que enseñar la discrepancia.
    lockedConditionPrices() {
      this.syncLockedActionFromConditions();
    },
    groups: {
      immediate: true,
      handler(val) {
        if (!this.activeGroup && val && val.length)
          this.activeGroup = val[0].id;
      }
    }
  },

  methods: {
    // ── Resumen footer ───────────────────────────────────────────────────
    // Returns { title, value, unit, spot } for one condition
    summaryConditionParts(cond) {
      const code = cond.rule_type_code;
      const def = this.typeDef(code);
      const title = def.title || code;
      const desc = conditionSummaryFields(code, def.settings_schema);
      const s = cond.settings || {};

      let value = "";
      let unit = desc.unit || "";
      let spot = "";

      if (desc.rangeFrom && desc.rangeTo) {
        const from = s[desc.rangeFrom] != null ? s[desc.rangeFrom] : "";
        const to = s[desc.rangeTo] != null ? s[desc.rangeTo] : "";
        value = from + "-" + to;
      } else if (desc.valueField) {
        value = s[desc.valueField] != null ? s[desc.valueField] : "";
      }

      if (desc.spotField) {
        spot = this.pickupLabel(s[desc.spotField] || "");
      } else if (desc.enumField) {
        const item = this.compsetItems.find(i => i.value === s[desc.enumField]);
        spot = item ? item.label : s[desc.enumField] || "";
      }

      return { title, value, unit, spot };
    },
    pickupLabel(spot) {
      if (!spot) return "";
      if (spot.startsWith("pickup-")) {
        const number = spot.split("-")[1];
        if (number === "0") return this.$t("pickup_0") || "OTB";
        try {
          const hotel =
            this.$store &&
            this.$store.getters &&
            this.$store.getters.current_hotel;
          if (hotel && hotel.settings && hotel.settings.pickup_spots) {
            const label = hotel.settings.pickup_spots["pickup_" + number];
            if (label) return this.$t("pickup") + " " + label;
          }
        } catch (e) {
          /* noop */
        }
        return this.$t("pickup") + " " + number;
      }
      return spot.replace("-", " ");
    },

    // ── Helpers de tipo ──────────────────────────────────────────────────
    typeDef(code) {
      return this.ruleTypes.find(t => t.code === code) || {};
    },
    // Returns the cached async component for a given rule_type_code.
    condBodyComponent(code) {
      return conditionComponent(code);
    },
    typeColor(code) {
      return this.typeDef(code).color || "#1576c7";
    },
    freqLabel(v) {
      const f = this.freqItems.find(i => i.value === v);
      return f ? f.text : v;
    },
    // ── Rutas y condiciones ──────────────────────────────────────────────
    addRoute() {
      if (this.rule.routes.length >= this.maxRoutes) return;
      // El valor de acción se crea con la ruta, no después, y copiado de la
      // anterior: la ruta nueva arranca con el valor que el usuario tiene
      // delante en vez de con el default del tipo. Es lo que permite que
      // activar el botón de valor por ruta no tenga que copiar nada.
      const routes = this.rule.routes;
      routes.push({
        id: ++this.seq,
        conditions: [],
        action: this.clonedRouteAction(routes[routes.length - 1])
      });
      this.fitToView();
    },
    askRemoveRoute(routeId) {
      const route = this.rule.routes.find(r => r.id === routeId);
      if (!route) return;
      if (route.conditions.length === 0) {
        this.removeRoute(routeId);
        return;
      }
      this.confirmDelete = { show: true, kind: "route", routeId, condId: null };
    },
    removeRoute(routeId) {
      if (this.rule.routes.length <= 1) return;
      this.rule.routes = this.rule.routes.filter(r => r.id !== routeId);
      this.fitToView();
    },
    openDrawer(routeId, index) {
      this.drawerTarget = { routeId, index, mode: "insert", condId: null };
      this.search = "";
      if (!this.activeGroup && this.groups.length)
        this.activeGroup = this.groups[0].id;
      this.drawer = true;
    },
    openReplaceDrawer(routeId, condId) {
      this.drawerTarget = { routeId, condId, mode: "replace", index: -1 };
      this.search = "";
      if (!this.activeGroup && this.groups.length)
        this.activeGroup = this.groups[0].id;
      this.drawer = true;
    },
    pickTrigger(code) {
      const route = this.rule.routes.find(
        r => r.id === this.drawerTarget.routeId
      );
      if (!route) return;

      const ruleType = this.typeDef(code);
      const settings = registryDefaultSettings(ruleType.settings_schema);

      if (this.drawerTarget.mode === "replace") {
        const condIdx = route.conditions.findIndex(
          c => c.id === this.drawerTarget.condId
        );
        if (condIdx === -1) return;
        route.conditions.splice(condIdx, 1, {
          id: route.conditions[condIdx].id,
          rule_type_code: code,
          settings
        });
      } else {
        if (route.conditions.length >= this.maxConditions) return;
        const idx = Math.min(this.drawerTarget.index, route.conditions.length);
        route.conditions.splice(idx, 0, {
          id: ++this.seq,
          rule_type_code: code,
          settings
        });
      }

      // El primer modo declarado por el tipo que además esté disponible en este
      // contexto. Los modos que declara el tipo no distinguen regla de
      // guardarail: varios empiezan por `set_value`, que sólo vale en
      // guardarailes, y aplicarlo a una regla dejaba el toggle sin selección y
      // la regla sin poder guardarse ("Invalid action.mode 'set_value'").
      const declared = actionModesFor(ruleType);
      this.rule.action.mode =
        declared.find(m => this.availableActionModes.indexOf(m) >= 0) ||
        this.availableActionModes[0];
      this.ensureRouteActions();
      this.drawer = false;
      this.fitToView();
    },
    askRemoveCondition(routeId, condId) {
      this.confirmDelete = { show: true, kind: "condition", routeId, condId };
    },
    confirmRemove() {
      const { kind, routeId, condId } = this.confirmDelete;
      this.confirmDelete = {
        show: false,
        kind: "condition",
        routeId: null,
        condId: null
      };
      if (kind === "route") this.removeRoute(routeId);
      else this.removeCondition(routeId, condId);
    },
    removeCondition(routeId, condId) {
      const route = this.rule.routes.find(r => r.id === routeId);
      if (!route) return;
      route.conditions = route.conditions.filter(c => c.id !== condId);
      this.fitToView();
    },

    // ── Acción ───────────────────────────────────────────────────────────
    setActionMode(m) {
      this.rule.action.mode = m;
    },
    // Cambio del botón "un solo valor / un valor por ruta".
    //
    // Se hace por @change y no por v-model para que la copia ocurra ANTES de
    // que el computed cambie y se repinte el bloque.
    //
    // Al encender: todas las rutas arrancan con el valor que el usuario tenía
    // delante, y desde ahí toca las que difieran. Al apagar: manda el valor de
    // la primera ruta, que es la que se ve arriba y la que ya se espejaba en la
    // acción global. Los buckets de las demás no se borran, así que apagar y
    // encender dentro de la misma sesión no es destructivo... salvo por los
    // valores que se sobrescriben aquí, que es el precio de que encender sea
    // predecible.
    applyValueScope(next) {
      const perRoute = next === true;
      const routes = this.rule.routes || [];
      const mode = this.rule.action.mode;
      const source = routes[0] && routes[0].action && routes[0].action[mode];
      if (perRoute && source) {
        routes.forEach(route => {
          if (!route.action)
            this.$set(route, "action", this.defaultRouteAction());
          Object.keys(source).forEach(field => {
            this.$set(route.action[mode], field, source[field]);
          });
        });
      }
      this.rule.action.per_route = perRoute;
    },
    amountDirectionLabelFor(amount) {
      return amount < 0 ? this.$t("decrease_price") : this.$t("increase_price");
    },
    // Rótulo del slot de una ruta. En ajuste manual dice si esa rama sube o
    // baja, que es lo útil cuando una hace +15 y la otra -10; en los demás
    // modos es el mismo para todas y lo da actionFieldLabel.
    slotFieldLabel(routeId) {
      if (this.rule.action.mode !== "manual") return this.actionFieldLabel;
      return this.amountDirectionLabelFor(
        this.routeAction(routeId).manual.amount
      );
    },
    actionModeLabel(m) {
      const labels = {
        manual: "manual_adjust",
        market: "market",
        set_value: "action_mode_set_value",
        notify: "action_mode_notify"
      };
      return this.$t(labels[m] || m);
    },

    // ── Valor de acción por ruta ─────────────────────────────────────────
    //
    // Se guarda un bucket por modo, creado entero de una vez (Vue 2 no hace
    // reactivas las claves añadidas después). Así cambiar de modo no convierte
    // ni resetea nada: simplemente revela el otro juego de valores. Convertir
    // 120€ a "middle" no significaría nada, y resetear al volver atrás sería
    // molesto.
    defaultRouteAction() {
      return {
        // La unidad (€/%) no está aquí: es de la regla, no de la ruta.
        manual: { amount: 0 },
        set_value: { value: this.defaultSetValue, unit: "fixed" },
        market: { compset_range: this.defaultCompsetRange },
        notify: { priority: this.defaultNotifyPriority }
      };
    },
    // Copia del bucket de otra ruta, para que una ruta nueva arranque con el
    // valor que el usuario está viendo y no con el default del tipo.
    clonedRouteAction(from) {
      const source = from && from.action;
      if (!source) return this.defaultRouteAction();
      const out = this.defaultRouteAction();
      Object.keys(out).forEach(mode => {
        if (source[mode]) out[mode] = { ...out[mode], ...source[mode] };
      });
      return out;
    },
    // Rutas que llegan sin `action`: drafts de localStorage anteriores al
    // cambio, semillas de plantilla y fixtures. $set porque restore()
    // reemplaza this.rule con JSON crudo.
    ensureRouteActions() {
      const routes = this.rule.routes || [];
      const seed = routes.find(route => route.action) || null;
      routes.forEach(route => {
        if (!route.action)
          this.$set(route, "action", this.clonedRouteAction(seed));
      });
    },
    // El botón es del canvas, no del payload: el backend deduce el alcance de
    // si las rutas traen bloque de valor. Los drafts guardados antes de que
    // existiera el botón no lo traen, y en Vue 2 una clave añadida después no
    // sería reactiva.
    ensureValueScopeFlag() {
      if (this.rule.action.per_route === undefined) {
        this.$set(this.rule.action, "per_route", false);
      }
    },
    // Texto de la acción para el resumen del pie. Con `route` usa el valor de
    // esa ruta; sin él, la acción global.
    actionTokenText(route) {
      const action = route
        ? effectiveActionFor(this.rule.action, route)
        : this.rule.action;
      if (action.mode === "manual") {
        const sign = action.amount > 0 ? "+" : "";
        return (
          sign + action.amount + " " + (action.unit === "fixed" ? "€" : "%")
        );
      }
      if (action.mode === "market") {
        const item = this.compsetItems.find(
          i => i.value === action.compset_range
        );
        return (
          this.$t("rule_summary_compset") +
          " " +
          (item ? item.label : action.compset_range)
        );
      }
      return actionPhrase(this, action);
    },
    routeAction(routeId) {
      const route = (this.rule.routes || []).find(r => r.id === routeId);
      return (route && route.action) || this.defaultRouteAction();
    },
    setRouteActionField(routeId, mode, field, value) {
      const route = (this.rule.routes || []).find(r => r.id === routeId);
      if (!route) return;
      if (!route.action) this.$set(route, "action", this.defaultRouteAction());
      this.$set(route.action[mode], field, value);
      if (mode === "set_value" && field === "value") {
        this.syncLockedConditionFromAction(route, value);
      }
    },

    // ── Guardarail fijo: el precio de la condición ES el valor de la acción ──
    //
    // Dice dos veces el mismo número —"si el precio calculado es menor que X" y
    // "ponlo a X"— porque su estructura es la de cualquier regla. Para el
    // usuario es un solo valor, así que se espejan.
    //
    // Los dos sentidos escriben EL MISMO número y el setter reactivo de Vue
    // corta en cuanto el valor no cambia, así que no hay realimentación. Eso
    // vale solo porque el valor es un primitivo: no espejar nunca objetos ni
    // arrays por aquí.
    //
    // Solo con `set_value`: en `market` o `notify` la acción no es un precio y
    // la condición vuelve a ser suya.
    syncLockedConditionFromAction(route, value) {
      if (this.lockedValueRoutes.indexOf(route) === -1) return;
      if (!Number.isFinite(value)) return;
      const condition = (route.conditions || [])[0];
      if (!condition || !condition.settings) return;
      const bounds = this.lockedConditionBounds(condition);
      const clamped = Math.min(bounds.max, Math.max(bounds.min, value));
      this.$set(condition.settings, "price", clamped);
    },
    syncLockedActionFromConditions() {
      this.lockedValueRoutes.forEach(route => {
        const price = lockedRoutePrice(route);
        if (!Number.isFinite(price)) return;
        const bounds = this.setValueBounds;
        const clamped = Math.min(bounds.max, Math.max(bounds.min, price));
        if (!route.action)
          this.$set(route, "action", this.defaultRouteAction());
        this.$set(route.action.set_value, "value", clamped);
      });
    },
    // Cotas del campo de la condición. Vienen del settings_schema del tipo, que
    // es otra fuente distinta de la de la acción (strategy_config): sin
    // recortar en cada sentido, el espejo colaría un valor fuera del rango del
    // otro lado, porque un :value programático no pasa por el clamp del campo.
    lockedConditionBounds(condition) {
      const schema = this.typeDef(condition.rule_type_code).settings_schema;
      const rules = (schema && schema.price) || [];
      const bounds =
        rules.find(r => r && typeof r === "object" && "min" in r) || {};
      return {
        min: bounds.min != null ? bounds.min : 0,
        max: bounds.max != null ? bounds.max : 9999
      };
    },
    // El suelo y el techo se identifican por el TIPO de condición, no por el
    // orden de las rutas, que el backend no garantiza.
    lockedBoundPrice(code) {
      const route = this.lockedValueRoutes.find(r =>
        (r.conditions || []).some(c => c.rule_type_code === code)
      );
      return route ? lockedRoutePrice(route) : null;
    },
    validateLockedGuardrail() {
      if (!this.lockedValueRoutes.length) return true;
      const min = this.lockedBoundPrice("price-lower-than");
      const max = this.lockedBoundPrice("price-greater-than");
      if (min == null || max == null) return true;
      if (!Number.isFinite(min) || !Number.isFinite(max)) {
        this.flash(this.$t("required"), "error");
        return false;
      }
      // Iguales tampoco vale: ese precio exacto se quedaría sin cubrir por
      // ninguna de las dos ramas.
      if (min >= max) {
        this.flash(this.$t("guardrail_min_max_invalid"), "error");
        return false;
      }
      return true;
    },
    // Valor de una ruta, aplanado para el payload. Quién lo llama decide si
    // acaba dentro de la ruta (valor por ruta) o en la acción global (valor
    // único). La unidad de `manual` nunca sale de aquí: es de la regla.
    serializeRouteAction(route) {
      const mode = this.rule.action.mode;
      const bucket = (route && route.action && route.action[mode]) || {};
      if (mode === "manual") {
        return { amount: bucket.amount };
      }
      if (mode === "set_value") {
        return { value: bucket.value, unit: bucket.unit || "fixed" };
      }
      if (mode === "market") {
        return { compset_range: bucket.compset_range };
      }
      if (mode === "notify") {
        return { priority: bucket.priority };
      }
      return null;
    },
    isCatActive(id) {
      const ids = this.rule.action.room_type_ids;
      if (id === "__all__") return ids.length >= this.categories.length;
      return ids.indexOf(id) > -1;
    },
    toggleCat(id) {
      if (id === "__all__") {
        this.rule.action.room_type_ids = this.isCatActive("__all__")
          ? []
          : this.categories.map(c => c.id);
        return;
      }
      const ids = this.rule.action.room_type_ids.slice();
      const i = ids.indexOf(id);
      if (i > -1) ids.splice(i, 1);
      else ids.push(id);
      this.rule.action.room_type_ids = ids;
    },

    // ── Zoom & Pan ───────────────────────────────────────────────────────
    zoomBy(delta) {
      const z = Math.min(
        1.6,
        Math.max(0.4, Math.round((this.zoom + delta) * 100) / 100)
      );
      const vp = this.$refs.viewport.getBoundingClientRect();
      const cx = vp.width / 2,
        cy = vp.height / 2;
      const ratio = z / this.zoom;
      this.pan.x = cx - (cx - this.pan.x) * ratio;
      this.pan.y = cy - (cy - this.pan.y) * ratio;
      this.zoom = z;
    },
    onWheel(e) {
      const z = Math.min(
        1.6,
        Math.max(0.4, Math.round((this.zoom - e.deltaY * 0.0012) * 100) / 100)
      );
      const vp = this.$refs.viewport.getBoundingClientRect();
      const cx = e.clientX - vp.left,
        cy = e.clientY - vp.top;
      const ratio = z / this.zoom;
      this.pan.x = cx - (cx - this.pan.x) * ratio;
      this.pan.y = cy - (cy - this.pan.y) * ratio;
      this.zoom = z;
    },
    startPan(e) {
      if (
        e.target.closest(".node-card") ||
        e.target.closest(".plus-node") ||
        e.target.closest(".minus-node") ||
        e.target.closest(".new-route-btn") ||
        e.target.closest(".empty-route") ||
        e.target.closest(".zoom-bar")
      )
        return;
      this.panning = true;
      this.panStart = {
        x: e.clientX,
        y: e.clientY,
        px: this.pan.x,
        py: this.pan.y
      };
      window.addEventListener("mousemove", this.onPanMove);
      window.addEventListener("mouseup", this.endPan);
    },
    onPanMove(e) {
      if (!this.panning) return;
      this.pan.x = this.panStart.px + (e.clientX - this.panStart.x);
      this.pan.y = this.panStart.py + (e.clientY - this.panStart.y);
    },
    endPan() {
      this.panning = false;
      window.removeEventListener("mousemove", this.onPanMove);
      window.removeEventListener("mouseup", this.endPan);
    },
    // settle=true: segunda pasada tras 350ms (los cuerpos de condición son
    // componentes async y la tarjeta crece al cargarse; el fit es idempotente)
    fitToView(settle) {
      // (settle puede ser un MouseEvent cuando viene del botón del zoom-bar)
      if (settle !== true) {
        clearTimeout(this._fitTimer);
        this._fitTimer = setTimeout(() => this.fitToView(true), 350);
      }
      // Doble nextTick: la primera pasada puede ser justo la que escribe
      // slotLayout, y entonces la card crece DESPUÉS de medir y el encuadre
      // sale corto. Se mide y se alinea, y en el siguiente tick ya se encuadra.
      this.$nextTick(() => {
        if (this.perRouteAction) this.measureRouteSlots();
        // Sin guarda: con valor único también hay que llamarla, para que
        // devuelva el desplazamiento a cero.
        this.measureActionFrame();
        this.$nextTick(this.fitToViewNow);
      });
    },
    fitToViewNow() {
      {
        const vp = this.$refs.viewport;
        // Bounds de la regla completa: trigger, accion y columna de rutas
        const parts = [
          this.$refs.trigger,
          this.$refs.action,
          this.$refs.routesCol
        ].filter(Boolean);
        if (!vp || parts.length < 2) return;

        // Recalcular conectores de forma síncrona: scheduleConnectors es
        // asíncrono y newRouteBtnPos debe estar fresco antes de medir
        this.updateConnectors();

        const vpRect = vp.getBoundingClientRect();
        const curZ = this.zoom;
        const padding = 60;

        // Posición de las tarjetas en content-space (coordenadas sin transform)
        // screen = vpRect.origin + pan + contentCoord * zoom
        // contentCoord = (screen - vpRect.origin - pan) / zoom
        let cLeft = Infinity,
          cTop = Infinity,
          cRight = -Infinity,
          cBottom = -Infinity;
        parts.forEach(el => {
          const r = el.getBoundingClientRect();
          cLeft = Math.min(cLeft, (r.left - vpRect.left - this.pan.x) / curZ);
          cTop = Math.min(cTop, (r.top - vpRect.top - this.pan.y) / curZ);
          cRight = Math.max(
            cRight,
            (r.right - vpRect.left - this.pan.x) / curZ
          );
          cBottom = Math.max(
            cBottom,
            (r.bottom - vpRect.top - this.pan.y) / curZ
          );
        });
        // El botón de nueva ruta puede no estar renderizado aún en su sitio:
        // usar su posición calculada (mismo espacio de coordenadas)
        if (this.newRouteBtnPos) {
          cBottom = Math.max(cBottom, this.newRouteBtnPos.y + 36);
        }

        const cardW = cRight - cLeft;
        const cardH = cBottom - cTop;
        const vw = vpRect.width;
        const vh = vpRect.height;

        let z = Math.min(
          1.1,
          (vw - padding * 2) / cardW,
          (vh - padding * 2) / cardH
        );
        z = Math.max(0.4, Math.round(z * 100) / 100);
        this.zoom = z;

        // Centrar el bloque de tarjetas: pan = (vp_center - cards_center * z)
        this.pan.x = vw / 2 - (cLeft + cardW / 2) * z;
        this.pan.y = vh / 2 - (cTop + cardH / 2) * z;
        this.scheduleConnectors();
      }
    },

    // ── Alineación de los slots de acción con sus rutas ──────────────────
    //
    // Se mide con offsetTop/offsetHeight relativos a routesCol (que ya es
    // position-relative), no con getBoundingClientRect: son valores de layout,
    // así que ignoran el scale() del ancestro y vienen redondeados, lo que
    // estabiliza la firma frente al ruido subpíxel del zoom fraccional.
    //
    // No hay bucle infinito con el ResizeObserver: .canvas-content es
    // inline-flex con align-start y los anchors tienen altura 0, así que nada
    // de lo que se escriba dentro del nodo de acción puede alterar routesCol.
    // La guarda _slotSig corta la segunda pasada.
    measureRouteSlots() {
      const col = this.$refs.routesCol;
      if (!col) return false;
      const items = [];
      (this.rule.routes || []).forEach(route => {
        const refArr = this.$refs["route-" + route.id];
        const el = Array.isArray(refArr) ? refArr[0] : refArr;
        if (el) {
          items.push({
            routeId: route.id,
            top: el.offsetTop,
            height: el.offsetHeight
          });
        }
      });
      const height = col.offsetHeight;
      const sig =
        height +
        "|" +
        items.map(i => i.routeId + ":" + i.top + ":" + i.height).join(",");
      if (sig === this._slotSig) return false;

      // Red de seguridad: si por un redondeo distinto bajo zoom fraccional la
      // firma oscilara, esto lo convierte en un aviso en vez de un cuelgue.
      // Se cuenta por ventana de tiempo, no en total: los cambios legítimos
      // (añadir una ruta, cargar un cuerpo de condición) van espaciados, y un
      // bucle de realimentación se dispara en milisegundos.
      const now = performance.now();
      if (now - (this._syncSince || 0) > 1000) {
        this._syncSince = now;
        this._syncPasses = 0;
      }
      this._syncPasses = (this._syncPasses || 0) + 1;
      if (this._syncPasses > 12) {
        // eslint-disable-next-line no-console
        console.warn(
          "[rule-canvas] el layout de slots no converge, se detiene"
        );
        return false;
      }
      this._slotSig = sig;
      this.slotLayout = { height, items };
      return true;
    },
    // La cabecera y el pie del nodo de acción no pertenecen a ninguna ruta, así
    // que desplazan el bloque de slots respecto de la columna de rutas. Con
    // anchors de altura 0 y align-center el desfase de cada fila se cancela y
    // queda un único escalar: (alto del pie - alto de la cabecera) / 2.
    //
    // transform no dispara ResizeObserver, así que esta escritura es inerte
    // para el ciclo de medición.
    measureActionFrame() {
      // Sin slots no hay nada que alinear, y el desplazamiento tiene que
      // volver a cero: se aplica siempre en el template, así que dejarlo
      // puesto descuadraría la tarjeta al pasar a valor único.
      if (!this.perRouteAction) {
        if (this.actionShiftY !== 0) this.actionShiftY = 0;
        return;
      }
      const head = this.$refs.actionHead,
        foot = this.$refs.actionFoot;
      if (!head || !foot) return;
      const shift = Math.round((foot.offsetHeight - head.offsetHeight) / 2);
      if (shift !== this.actionShiftY) this.actionShiftY = shift;
    },

    // ── Conectores ───────────────────────────────────────────────────────
    // setTimeout + ResizeObserver (no rAF: puede frenarse en segundo plano).
    scheduleConnectors() {
      setTimeout(this.updateConnectors, 0);
      clearTimeout(this._settleTimer);
      this._settleTimer = setTimeout(this.updateConnectors, 350);
    },
    updateConnectors() {
      const content = this.$refs.content,
        trigger = this.$refs.trigger,
        action = this.$refs.action;
      if (!content || !trigger || !action) return;

      // escala REAL del DOM (estable durante transiciones de zoom/fit)
      let z = this.zoom;
      try {
        const tf = getComputedStyle(content).transform;
        if (tf && tf !== "none") {
          const m = new DOMMatrix(tf);
          if (m && m.a) z = m.a;
        }
      } catch (e) {
        z = this.zoom;
      }

      const cr = content.getBoundingClientRect();
      const box = el => {
        const r = el.getBoundingClientRect();
        return {
          x: (r.left - cr.left) / z,
          y: (r.top - cr.top) / z,
          w: r.width / z,
          h: r.height / z
        };
      };
      // Medir y alinear los slots antes de calcular nada
      if (this.perRouteAction) this.measureRouteSlots();
      this.measureActionFrame();

      const tb = box(trigger),
        ab = box(action);
      const tRight = { x: tb.x + tb.w, y: tb.y + tb.h / 2 };
      const aLeft = { x: ab.x, y: ab.y + ab.h / 2 };

      // Punto de entrada a la acción para una ruta. Con valor por ruta es el
      // centro de SU slot; si el slot aún no está en el DOM (primera pasada)
      // se cae a la Y de la ruta, que es donde acabará.
      const actionAnchor = row => {
        if (!this.perRouteAction) return aLeft;
        const refArr = this.$refs["action-slot-" + row.route.id];
        const el = Array.isArray(refArr) ? refArr[0] : refArr;
        if (!el) return { x: aLeft.x, y: row.midY };
        const sb = box(el);
        return { x: aLeft.x, y: sb.y + sb.h / 2 };
      };

      const rows = [];
      this.rule.routes.forEach(route => {
        const refArr = this.$refs["route-" + route.id];
        const el = Array.isArray(refArr) ? refArr[0] : refArr;
        if (!el) return;
        const rb = box(el);
        rows.push({
          route,
          left: rb.x,
          right: rb.x + rb.w,
          midY: rb.y + rb.h / 2,
          bottom: rb.y + rb.h
        });
      });
      if (!rows.length) {
        this.connectorPaths = [];
        this.plusNodes = [];
        this.minusNodes = [];
        this.newRouteBtnPos = null;
        this._connSig = "";
        return;
      }

      // firma: solo recalcula/re-renderiza si el layout cambió de verdad.
      // Las Y de los anclajes van explícitas: dos rutas que intercambien
      // alturas manteniendo el total no moverían el centro de la card, y sin
      // ellas se haría early-return con conectores obsoletos.
      const sig = JSON.stringify({
        t: [Math.round(tRight.x), Math.round(tRight.y)],
        a: [Math.round(aLeft.x), Math.round(aLeft.y)],
        s: rows.map(r => Math.round(actionAnchor(r).y)),
        sh: this.actionShiftY,
        r: rows.map(r => [
          Math.round(r.left),
          Math.round(r.right),
          Math.round(r.midY),
          Math.round(r.bottom),
          r.route.conditions.length
        ])
      });
      if (sig === this._connSig) return;
      this._connSig = sig;

      // dos columnas fijas (ramificación / fusión) → "+" alineados y tramos
      // rectos hasta el borde exacto de cada tarjeta
      const minLeft = Math.min.apply(
        null,
        rows.map(r => r.left)
      );
      const maxRight = Math.max.apply(
        null,
        rows.map(r => r.right)
      );
      const branchX = (tRight.x + minLeft) / 2;
      const mergeX = (maxRight + aLeft.x) / 2;
      const stroke = RC_CONNECTOR_STROKE;

      const paths = [],
        plus = [],
        minus = [];
      rows.forEach(r => {
        const bp = { x: branchX, y: r.midY };
        const mp = { x: mergeX, y: r.midY };
        paths.push({ d: this.curve(tRight, bp), color: stroke });
        paths.push({
          d: "M " + bp.x + " " + bp.y + " L " + r.left + " " + r.midY,
          color: stroke
        });
        paths.push({
          d: "M " + r.right + " " + r.midY + " L " + mp.x + " " + mp.y,
          color: stroke
        });
        // Con valor por ruta cada curva entra a la altura de SU slot; cuando
        // la alineación está asentada degenera en una recta horizontal.
        paths.push({ d: this.curve(mp, actionAnchor(r)), color: stroke });
        // "−" en la bifurcación: borra la ruta (también las vacías)
        minus.push({ x: bp.x, y: bp.y, routeId: r.route.id });
        // "+" pegado a la última card de SU ruta (no en la fusión global)
        if (r.route.conditions.length > 0) {
          plus.push({
            x: r.right + 32,
            y: r.midY,
            routeId: r.route.id,
            index: r.route.conditions.length
          });
        }
      });
      this.connectorPaths = paths;
      this.plusNodes = plus;
      this.minusNodes = minus;
      // Botón "nueva ruta": centrado respecto al diagrama, bajo todas las rutas
      const maxRouteBottom = Math.max.apply(
        null,
        rows.map(r => r.bottom)
      );
      this.newRouteBtnPos = {
        x: (tb.x + ab.x + ab.w) / 2,
        // Con slots por ruta la card baja más que las rutas: el botón no debe
        // quedar solapado con su pie.
        y: Math.max(maxRouteBottom + 56, ab.y + ab.h + 24)
      };
      // Calcular svgSize desde los bounds reales de las tarjetas para evitar
      // el problema circular (scrollWidth incluye el propio SVG absoluto)
      const allX = [
        tb.x,
        tb.x + tb.w,
        ab.x,
        ab.x + ab.w,
        ...rows.flatMap(r => [r.left, r.right])
      ];
      const allY = [
        tb.y,
        tb.y + tb.h,
        ab.y,
        ab.y + ab.h,
        maxRouteBottom + 100,
        ...rows.flatMap(r => [r.midY - 100, r.midY + 100])
      ];
      const maxX = Math.max(...allX);
      const maxY = Math.max(...allY);
      this.svgSize = {
        w: Math.ceil(maxX) + 120,
        h: Math.ceil(maxY) + 120
      };
    },
    curve(a, b) {
      const dx = Math.max(46, Math.abs(b.x - a.x) * 0.5);
      return (
        "M " +
        a.x +
        " " +
        a.y +
        " C " +
        (a.x + dx) +
        " " +
        a.y +
        ", " +
        (b.x - dx) +
        " " +
        b.y +
        ", " +
        b.x +
        " " +
        b.y
      );
    },

    // ── Guardar / Cancelar ───────────────────────────────────────────────
    // Serializa el borrador al shape del backend. Lo comparten guardar y
    // probar: si divergieran, se probaría una regla distinta de la que se
    // acabaría guardando.
    buildRulePayload() {
      const roomTypeIds = (this.rule.action.room_type_ids || []).filter(
        id => id !== "__all__"
      );

      const hotel = this.$store.getters.current_hotel;

      // Build v2 routes payload (strip canvas-internal ids).
      //
      // Se descartan las rutas sin condiciones: una lista de condiciones vacía
      // es "siempre cierto" para casi cualquier evaluador, y con valor por
      // ruta eso sería un guardarail que dispara siempre.
      const usableRoutes = (this.rule.routes || []).filter(
        route => (route.conditions || []).length > 0
      );

      // Que las rutas lleven bloque de valor ES el contrato: el backend usa el
      // de la ruta si está y cae a la acción global si no. Se vuelve a mirar
      // sobre `usableRoutes` porque al descartar rutas vacías puede quedar una
      // sola, y una regla de una ruta no es un valor por ruta.
      const emitPerRoute = this.perRouteAction && usableRoutes.length > 1;

      const routes = usableRoutes.map(route => {
        const payload = {
          conditions: (route.conditions || []).map(cond => ({
            rule_type_code: cond.rule_type_code,
            settings: cond.settings || {}
          }))
        };
        if (emitPerRoute) payload.action = this.serializeRouteAction(route);
        return payload;
      });

      // Build v2 action payload.
      //
      // La acción global se compone siempre, con el valor de la primera ruta:
      // en valor único es EL valor, y con valor por ruta es el espejo que ve un
      // consumidor que solo lea la acción global. La unidad de `manual` va aquí
      // en los dos casos, que es de la regla.
      const mode = this.rule.action.mode;
      const action = {
        mode,
        ...this.serializeRouteAction(usableRoutes[0])
      };
      if (mode === "manual") action.unit = this.rule.action.unit;

      return {
        hotel_id: hotel && hotel.id,
        rule_kind: this.mode,
        name: this.rule.name || "",
        color: this.effectiveColor,
        room_type_ids: roomTypeIds,
        // Un guardarail no tiene horario propio: se evalúa tras cada cálculo
        schedule: this.isGuardrail
          ? { frequency: "on_change" }
          : this.rule.schedule,
        routes,
        action
      };
    },
    onSave() {
      // Validate: at least one route with one condition
      const hasCondition = (this.rule.routes || []).some(
        r => r.conditions && r.conditions.some(c => c.rule_type_code)
      );
      if (!hasCondition) {
        this.flash(this.$t("required"), "error");
        return;
      }
      if (!this.validateLockedGuardrail()) return;

      // Guardar sin nombre deja reglas imposibles de distinguir en la
      // biblioteca; se le pone el que ya anunciaba el placeholder.
      if (!(this.rule.name || "").trim() && this.autoRuleName) {
        this.rule.name = this.autoRuleName;
      }

      const data = this.buildRulePayload();
      const ruleToEdit = this.$store.state.pricingstrategy.rule_to_edit;

      const isEdit = !!(ruleToEdit && ruleToEdit.id);
      if (isEdit) data.rule_id = ruleToEdit.id;

      const prefix = this.isGuardrail ? "guardrail" : "rule";
      const snackbarPending = isEdit
        ? `updating_${prefix}`
        : `adding_${prefix}`;
      const snackbarDone = isEdit ? `${prefix}_updated` : `${prefix}_added`;
      const snackbarError = isEdit
        ? `${prefix}_not_updated`
        : `${prefix}_not_added`;
      const storeAction = isEdit
        ? "setPricingStrategyRule"
        : "addPricingStrategyRule";

      this.$store.dispatch("setSnackbar", {
        title: this.$t(snackbarPending),
        color: "secondary"
      });
      this.$store
        .dispatch(storeAction, data)
        .then(response => {
          this.$store.dispatch("setSnackbar", {
            title: this.$t(snackbarDone),
            color: "accent"
          });
          if (!isEdit && response && response.data) {
            if (this.isGuardrail) {
              // Un guardarail no se coloca en el grid: se engancha al final
              // de la lista de la estrategia.
              return this.attachGuardrail(response.data.id).then(() => {
                this._closeDialog();
              });
            }
            // Al CREAR una regla, activar el modo de colocación en el grid
            this.$store.dispatch(
              "setPricingStrategyRuleToPlace",
              response.data
            );
          }
          this._closeDialog();
        })
        .catch(() => {
          // El diálogo se queda abierto: cerrarlo tiraba lo que el usuario
          // acabara de escribir y le dejaba sin manera de reintentar.
          Utils.showError(this, snackbarError);
        });
    },
    // Engancha un guardarail recién creado al final de la estrategia
    attachGuardrail(ruleId) {
      const ps = this.$store.state.pricingstrategy;
      const strategy = ps.strategy || {};
      const current = strategy.strategyGuardrails || [];
      const hotel = this.$store.getters.current_hotel;
      return this.$store.dispatch("setPricingStrategyStrategyGuardrail", {
        strategy_id: strategy.id,
        hotel_id: hotel && hotel.id,
        rule_id: ruleId,
        position: current.filter(g => !g.locked).length
      });
    },
    // ── Probar la regla ──────────────────────────────────────────────────
    // Manda el borrador sin guardar y lo evalúa el motor de verdad contra los
    // datos reales de la fecha. Lo que se prueba es lo que pasará.
    onTestRule() {
      const hasCondition = (this.rule.routes || []).some(
        r => r.conditions && r.conditions.some(c => c.rule_type_code)
      );
      if (!hasCondition) {
        this.flash(this.$t("required"), "error");
        return;
      }

      this.testing_rule = true;
      this.$store
        .dispatch("testPricingStrategyRule", {
          ...this.buildRulePayload(),
          strategy_id: (this.$store.state.pricingstrategy.strategy || {}).id,
          target_date: this.test_target_date
        })
        .then(response => {
          this.test_result = (response && response.data) || null;
          this.test_menu = false;
          this.test_dialog = true;
        })
        .catch(() => Utils.showError(this, "test_rule_failed"))
        .finally(() => {
          this.testing_rule = false;
        });
    },
    onCancel() {
      this._closeDialog();
      this.$emit("cancel");
    },
    _closeDialog() {
      this.$store.dispatch("setPricingStrategyRuleToEdit", null);
      this.$store.dispatch("setPricingStrategyRuleDefinitionDialog", false);
    },
    flash(text, color) {
      this.snackbar = { show: true, text, color };
    },

    // Color aleatorio de la paleta de la cadena (los seleccionables en el
    // color-palette). Fallback a RC_PRIMARY si la cadena no tiene colores.
    pickRandomRuleColor() {
      const chain = this.$store && this.$store.state.chain.chain;
      return randomRuleColor(chain && chain.colors, RC_PRIMARY);
    },
    // Valor de acción de una ruta al leerla del backend.
    //
    // Discrimina por NOMBRE DE CLAVE y no por modo, así no depende de que
    // action.mode y el bucket estén sincronizados y sigue funcionando si el
    // backend añade campos.
    routeActionFromBackend(globalAction, rawRouteAction) {
      const out = this.defaultRouteAction();
      const global = globalAction || {};

      // 1) semilla desde la acción global: una regla antigua tiene una única
      //    acción, así que todas sus rutas arrancan con el mismo valor y al
      //    reguardar vuelve a emitirse el mismo payload
      if (global.amount != null) out.manual.amount = global.amount;
      if (global.compset_range) out.market.compset_range = global.compset_range;
      if (global.value != null) out.set_value.value = global.value;
      if (global.priority != null) out.notify.priority = global.priority;

      // 2) el valor propio de la ruta pisa la semilla
      const raw = rawRouteAction || {};
      if (raw.amount != null) out.manual.amount = raw.amount;
      if (raw.value != null) out.set_value.value = raw.value;
      if (raw.unit) out.set_value.unit = raw.unit;
      if (raw.compset_range) out.market.compset_range = raw.compset_range;
      if (raw.priority != null) out.notify.priority = raw.priority;
      return out;
    },
    // ── Mapper backend → canvas ──────────────────────────────────────────
    buildRuleFromBackend(r) {
      if (!r) return null;
      // Respetar el color existente si es válido; si falta o es el gris de
      // fondo neutro (reglas nuevas o datos antiguos), escoger uno aleatorio
      // de la paleta de la cadena (los seleccionables en pantalla).
      const color = isNeutralRuleColor(r.color)
        ? this.pickRandomRuleColor()
        : r.color;
      const roomTypeIds = Array.isArray(r.room_type_ids)
        ? r.room_type_ids.map(x => (x && typeof x === "object" ? x.id : x))
        : [];

      const defaultSchedule = {
        frequency: this.defaultFrequency,
        hourly: { ranges: [{ from: "08:00", to: "20:00" }] },
        daily: { ranges: [{ from: "08:00", to: "20:00" }] },
        weekly: { weekday: 1, time: "09:00" },
        monthly: { day_of_month: 1, time: "09:00" }
      };

      if (!r.id && !(r.routes && r.action)) {
        // Nueva regla en blanco (o con un hint suelto rule_type_code).
        // Un seed de plantilla trae routes+action y cae en la rama v2 de abajo.
        const routes = [
          { id: 1, conditions: [], action: this.defaultRouteAction() }
        ];
        if (r.rule_type_code) {
          routes[0].conditions.push({
            id: 11,
            rule_type_code: r.rule_type_code,
            settings: this.defaultCanvasConditionSettings(r.rule_type_code)
          });
        }
        return {
          name: r.name || "",
          color,
          schedule: r.schedule || defaultSchedule,
          routes,
          action: {
            mode: r.rule_type_code
              ? actionModesFor(this.typeDef(r.rule_type_code))[0]
              : this.isGuardrail
              ? this.defaultGuardrailMode
              : this.defaultMode,
            amount: 0,
            unit: this.defaultUnit,
            compset_range: this.defaultCompsetRange,
            priority: this.defaultNotifyPriority,
            per_route: false,
            room_type_ids: roomTypeIds
          }
        };
      }

      // Edición de regla existente — soporta v2 (routes/action/schedule) y v1 (settings plano)
      if (r.routes && r.action) {
        // v2 shape
        const routes = r.routes.map((route, ri) => ({
          id: ri + 1,
          conditions: (route.conditions || []).map((cond, ci) => ({
            id: (ri + 1) * 10 + ci + 1,
            rule_type_code: cond.rule_type_code,
            settings: cond.settings || {}
          })),
          action: this.routeActionFromBackend(r.action, route.action)
        }));
        // El modo `clamp` se retiró; queda el alias por si hay algún draft de
        // localStorage anterior al cambio.
        const mode =
          (this.isGuardrail && GUARDRAIL_MODE_ALIASES[r.action.mode]) ||
          r.action.mode ||
          "manual";
        return {
          name: r.name || "",
          color,
          schedule: r.schedule
            ? { ...defaultSchedule, ...r.schedule }
            : defaultSchedule,
          routes,
          action: {
            mode,
            amount: r.action.amount || 0,
            unit: r.action.unit || "fixed",
            compset_range: r.action.compset_range || "middle",
            priority:
              r.action.priority != null
                ? r.action.priority
                : this.defaultNotifyPriority,
            // El alcance no viaja en el payload: se deduce de si las rutas
            // traen bloque de valor, que es el contrato del backend. Se mira
            // sobre `r` crudo, antes de que el map de arriba le dé bucket a
            // todas las rutas.
            per_route: hasPerRouteAction(r),
            room_type_ids: roomTypeIds
          }
        };
      }

      // v1 fallback (legacy rules from old backend)
      const s = r.settings || {};
      const legacyAction = {
        mode: actionMode(this.typeDef(r.rule_type_code).settings_schema),
        amount: s.amount || 0,
        unit: s.unit || "fixed",
        compset_range: s.compset_range || "middle",
        priority: this.defaultNotifyPriority,
        per_route: false,
        room_type_ids: roomTypeIds
      };
      return {
        name: r.name || "",
        color,
        schedule: defaultSchedule,
        routes: [
          {
            id: 1,
            conditions: [
              {
                id: 11,
                rule_type_code: r.rule_type_code,
                settings: this.extractConditionSettings(r.rule_type_code, s)
              }
            ],
            // Sembrado desde la acción legacy: el bucket de la ruta es lo que
            // editan los controles, así que sin esto el importe de una regla
            // v1 se perdería al abrirla.
            action: this.routeActionFromBackend(legacyAction, null)
          }
        ],
        action: legacyAction
      };
    },
    // Extrae solo campos de condicion del settings del backend
    // (excluye evaluation_frequency y campos de accion)
    extractConditionSettings(code, settings) {
      const ACTION_FIELDS = [
        "evaluation_frequency",
        "amount",
        "unit",
        "compset_range"
      ];
      const schema = this.typeDef(code).settings_schema || {};
      const conditionKeys = Object.keys(schema);
      if (conditionKeys.length) {
        // Extraer solo los campos definidos en el schema del backend
        return Object.fromEntries(
          conditionKeys.map(k => [
            k,
            settings[k] !== undefined ? settings[k] : null
          ])
        );
      }
      // Si no hay schema todavía, excluir campos de acción conocidos
      return Object.fromEntries(
        Object.entries(settings).filter(([k]) => !ACTION_FIELDS.includes(k))
      );
    },
    // Settings de condicion por defecto para una regla nueva con tipo conocido
    defaultCanvasConditionSettings(code) {
      return registryDefaultSettings(this.typeDef(code).settings_schema);
    },

    // ── Persistencia opcional de borrador ────────────────────────────────
    persist() {
      if (!this.draftKey) return;
      try {
        localStorage.setItem(this.draftKey, JSON.stringify(this.rule));
      } catch (e) {
        /* noop */
      }
    },
    restore() {
      if (!this.draftKey || this.initialRule) return;
      try {
        const raw = localStorage.getItem(this.draftKey);
        if (raw) {
          const saved = JSON.parse(raw);
          if (saved && saved.routes) this.rule = saved;
        }
      } catch (e) {
        /* noop */
      }
    },
    initSeq() {
      let maxId = this.seq;
      this.rule.routes.forEach(route => {
        if (route.id > maxId) maxId = route.id;
        route.conditions.forEach(c => {
          if (c.id > maxId) maxId = c.id;
        });
      });
      this.seq = maxId;
    }
  },

  created() {
    // Leer rule_to_edit del store y construir el modelo interno.
    // Si no hay store (standalone/Storybook), usar initialRule prop o los datos de demo.
    const storeRule =
      this.$store &&
      this.$store.state.pricingstrategy &&
      this.$store.state.pricingstrategy.rule_to_edit;
    if (storeRule) {
      const built = this.buildRuleFromBackend(storeRule);
      if (built) this.rule = built;
    } else if (this.initialRule) {
      this.rule = JSON.parse(JSON.stringify(this.initialRule));
    }
    this.ensureRouteActions();
    this.ensureValueScopeFlag();
  },

  mounted() {
    this.restore();
    // restore() reemplaza this.rule con JSON crudo del localStorage, que
    // puede ser anterior al valor de acción por ruta
    this.ensureRouteActions();
    this.ensureValueScopeFlag();
    this.initSeq();
    this.$nextTick(() => {
      // updateConnectors primero (actualiza svgSize desde el DOM real),
      // luego fitToView (usa el scrollWidth correcto, sin el SVG inicial de 1600px)
      this.updateConnectors();
      this.fitToView();
      [350, 700].forEach(d =>
        setTimeout(() => {
          this.updateConnectors();
          this.fitToView();
        }, d)
      );
      if (window.ResizeObserver) {
        this._ro = new ResizeObserver(() => this.scheduleConnectors());
        [this.$refs.trigger, this.$refs.action, this.$refs.routesCol]
          .filter(Boolean)
          .forEach(el => this._ro.observe(el));
      }
    });
    window.addEventListener("resize", this.scheduleConnectors);
  },

  beforeDestroy() {
    window.removeEventListener("resize", this.scheduleConnectors);
    if (this._ro) this._ro.disconnect();
    clearTimeout(this._fitTimer);
    clearTimeout(this._settleTimer);
    this.endPan();
  }
};
</script>

<style scoped lang="scss">
.rule-canvas-root {
  /* brand */
  --rc-primary: #2e21bd;
  --rc-secondary: #1576c7;
  --rc-primary-bg: rgba(46, 33, 189, 0.06);
  --rc-action-border: #d9d3f7;

  /* text (light surfaces) */
  --rc-text: #1d2330;
  --rc-text-mid: #5a6172;
  --rc-text-muted: #8b92a3;

  /* borders */
  --rc-border: #e9ebef;
  --rc-border-light: #eef0f3;
  --rc-border-dashed: #c2c7d2;

  /* surfaces */
  --rc-surface: #ffffff;
  --rc-surface-hover: #f4f5f7;
  --rc-canvas-bg: #f4f5f8;
  --rc-canvas-dot: #c4cad4; /* hardcoded in SVG data-URI below, keep in sync */

  /* connector lines (SVG paths + AND line share the same value) */
  --rc-connector: #c7ccd6;

  /* accent */
  --rc-accent-green: #1b998b;
  --rc-accent-green-bg: #eafaf3;
  --rc-danger: #e53935;

  /* overlays / shadows */
  --rc-overlay: rgba(29, 35, 48, 0.28);
  --rc-shadow-badge: 0 4px 12px -3px rgba(0, 0, 0, 0.35);
  --rc-shadow-card: 0 10px 30px -16px rgba(31, 40, 61, 0.28);
  --rc-shadow-action: 0 14px 34px -16px rgba(46, 33, 189, 0.3);
  --rc-shadow-zoom: 0 8px 22px -12px rgba(31, 40, 61, 0.35);
  --rc-shadow-hover: 0 8px 20px -12px rgba(46, 33, 189, 0.45);
  --rc-shadow-plus: 0 4px 12px -4px rgba(46, 33, 189, 0.5);
  --rc-shadow-minus: 0 4px 12px -4px rgba(229, 57, 53, 0.5);

  /* summary bar (dark footer) */
  --rc-dark-bg: #1e293b;
  --rc-dark-border: #334155;
  --rc-dark-check-bg: rgba(52, 211, 153, 0.12);
  --rc-success: #34d399;
  --rc-dark-text: #94a3b8;
  --rc-dark-bright: #f1f5f9;
  --rc-dark-dim: #475569;
  --rc-op-and: #818cf8;
  --rc-op-or: #fbbf24;

  min-height: 600px;
  font-family: "Roboto", sans-serif;
}

/* ── Cabecera ─────────────────────────────────────────────────────────── */
.rule-header {
  height: 70px;
  gap: 14px;
  z-index: 8;
  border-bottom: 1px solid var(--rc-border);
}
.rule-badge {
  width: 38px;
  height: 38px;
  box-shadow: var(--rc-shadow-badge);
  transition: transform 0.15s ease;
  &:hover {
    transform: translateY(-1px) scale(1.03);
  }
}
.rule-name-input {
  min-width: 300px;
  ::v-deep .v-input__slot {
    padding: 0 6px !important;
    box-shadow: none !important;
    min-height: unset !important;
  }
  ::v-deep .v-input__control {
    min-height: unset !important;
  }
  ::v-deep input {
    font-size: 16px;
    font-weight: 700;
    color: var(--rc-text);
    padding: 2px 0;
  }
}
.rc-label {
  letter-spacing: 0.04em;
}
.color-chip {
  gap: 6px;
  letter-spacing: 0.08em;
}
.color-chip-dot {
  width: 11px;
  height: 11px;
}

/* ── Lienzo / viewport ────────────────────────────────────────────────── */
.canvas-viewport {
  background-color: var(--rc-canvas-bg);
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='22' height='22' viewBox='0 0 22 22'%3E%3Ccircle cx='11' cy='11' r='0.5' fill='%23c4cad4'/%3E%3C/svg%3E");
  background-size: 22px 22px;
  background-repeat: repeat;
  cursor: grab;
  &.is-panning {
    cursor: grabbing;
  }
}
.canvas-content {
  top: 0;
  left: 0;
  transform-origin: 0 0;
  gap: 132px;
  padding: 80px;
  will-change: transform;
}
.node-anchor {
  height: 0;
}
.connectors {
  top: 0;
  left: 0;
  pointer-events: none;
  z-index: 0;
}

/* ── Tarjetas base ────────────────────────────────────────────────────── */
.node-card {
  position: relative;
  z-index: 1;
  width: 300px;
  background: var(--rc-surface);
  border: 1px solid var(--rc-border);
  box-shadow: var(--rc-shadow-card);
  &.is-trigger {
    width: 215px;
  }
  &.is-action {
    width: 320px;
    border-color: var(--rc-action-border);
    box-shadow: var(--rc-shadow-action);
  }
  ::v-deep .v-input--slider {
    margin-top: 2px;
  }
  ::v-deep .v-input {
    font-size: 13px;
  }
}

/* Toggles que ocupan el ancho de la card y se reparten a partes iguales.
   Hace falta recortar el padding y el letter-spacing de v-btn porque los tres
   modos ("Ajuste manual", "Mercado", "Notificación") se pasan por poco de los
   276px útiles con los valores por defecto. */
.action-mode-toggle ::v-deep .v-btn,
.action-toggle-fill ::v-deep .v-btn {
  flex: 1 1 0;
  min-width: 0;
  padding: 0 4px;
  letter-spacing: 0.02em;
}

/* Switch sin etiqueta: sobra el hueco que Vuetify le reserva a su derecha. */
.action-scope-switch ::v-deep .v-input--selection-controls__input {
  margin-right: 0;
}

/* Slots de acción: uno por ruta, alineado con la fila de su ruta */
.action-slots {
  min-height: 88px;
}
.action-slot {
  left: 0;
  right: 0;
}
.action-slot-inner {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.action-slot-badge {
  line-height: 1;
  /* Los slots tienen el alto de su fila de ruta: recorta en vez de crecer y
     descuadrar la alineación con las rutas. */
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* conectores "+" (añadir condición) y "−" (borrar ruta) */
.plus-node,
.minus-node {
  position: absolute;
  z-index: 3;
  width: 26px;
  height: 26px;
  background: var(--rc-surface);
  border: 1.5px dashed var(--rc-border-dashed);
  color: var(--rc-text-muted);
  transform: translate(-50%, -50%);
  transition: all 0.15s ease;
  .v-icon {
    color: inherit;
  }
}
.plus-node:hover {
  border-color: var(--rc-primary);
  color: var(--rc-primary);
  border-style: solid;
  box-shadow: var(--rc-shadow-plus);
}
.minus-node:hover {
  border-color: var(--rc-danger);
  color: var(--rc-danger);
  border-style: solid;
  box-shadow: var(--rc-shadow-minus);
}

/* botón "nueva ruta" anclado bajo las rutas, centrado en el diagrama */
.new-route-anchor {
  position: absolute;
  z-index: 2;
  transform: translate(-50%, 0);
}
.new-route-btn {
  background: var(--rc-surface);
}

/* fila de ruta (condiciones en AND) */
.and-link {
  width: 56px;
  position: relative;
}
.and-line {
  height: 2px;
  width: 100%;
  background: var(--rc-connector);
}
.and-ball {
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  width: 26px;
  height: 26px;
  background: var(--rc-surface);
  border: 1.5px solid var(--rc-border-dashed);
  color: var(--rc-text-mid);
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 0.02em;
  z-index: 2;
}
.empty-route {
  width: 296px;
  height: 116px;
  gap: 6px;
  border: 1.5px dashed var(--rc-border-dashed);
  background: rgba(255, 255, 255, 0.4);
  transition: all 0.15s ease;
  &:hover {
    border-color: var(--rc-primary);
    color: var(--rc-primary) !important;
    background: var(--rc-surface);
  }
}

/* control de zoom */
.zoom-bar {
  position: absolute;
  bottom: 20px;
  left: 20px;
  z-index: 6;
  gap: 2px;
  border: 1px solid var(--rc-border);
  box-shadow: var(--rc-shadow-zoom);
}
.zoom-val {
  min-width: 42px;
  color: var(--rc-text);
}

/* ── Panel "añadir condición" ─────────────────────────────────────────── */
.drawer-overlay {
  inset: 0;
  background: var(--rc-overlay);
  z-index: 200;
}
.drawer-panel {
  width: 420px;
  border-left: 1px solid var(--rc-border-light);
}
.drawer-head {
  border-bottom: 1px solid var(--rc-border-light);
}
.cat-side {
  width: 168px;
  background: var(--rc-surface-hover);
  border-right: 1px solid var(--rc-border-light);
  .cs-item {
    border-bottom: 1px solid var(--rc-border-light);
    border-left: 4px solid transparent;
    transition: background 0.12s ease, border-color 0.12s ease;
    &:hover:not(.active) {
      background: rgba(255, 255, 255, 0.55);
    }
    &.active {
      background: var(--rc-surface);
      border-left-color: var(--rc-accent-green);
      .cs-name {
        color: var(--rc-accent-green);
        font-weight: 700;
      }
      .cs-icon {
        color: var(--rc-accent-green) !important;
      }
    }
  }
  .cs-name {
    font-weight: 500;
    color: var(--rc-text-mid);
    line-height: 1.25;
  }
  .cs-icon {
    color: var(--rc-text-muted) !important;
  }
}
.trigger-item {
  transition: all 0.14s ease;
  &:hover {
    border-color: var(--rc-primary) !important;
    box-shadow: var(--rc-shadow-hover);
    transform: translateY(-1px);
  }
}

/* Animación del panel */
.drawer-slide-enter-active,
.drawer-slide-leave-active {
  transition: opacity 0.2s ease;
  .drawer-panel {
    transition: transform 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  }
}
.drawer-slide-enter,
.drawer-slide-leave-to {
  opacity: 0;
  .drawer-panel {
    transform: translateX(100%);
  }
}

/* ── Barra de resumen de la regla ─────────────────────────────────────── */
.rule-summary-bar {
  height: 40px;
  background: var(--rc-dark-bg);
  border-top: 1px solid var(--rc-dark-border);
  z-index: 8;
}
.rsb-check {
  width: 22px;
  height: 22px;
  background: var(--rc-dark-check-bg);
}
.rsb-text {
  color: var(--rc-dark-text);
}
/* freq + action: same green accent, bold */
.rsb-freq,
.rsb-action {
  color: var(--rc-success);
  font-weight: 700;
}
/* title + value: bright white text */
.rsb-title,
.rsb-value {
  color: var(--rc-dark-bright);
}
.rsb-title {
  font-weight: 600;
}
.rsb-value {
  font-weight: 700;
}
/* sep + empty: dimmed; empty adds italic */
.rsb-sep,
.rsb-empty {
  color: var(--rc-dark-dim);
}
.rsb-empty {
  font-style: italic;
}
/* operators */
.rsb-and,
.rsb-or {
  font-weight: 700;
}
.rsb-and {
  color: var(--rc-op-and);
}
.rsb-or {
  color: var(--rc-op-or);
}
</style>
